// ============================================================
// AdminPage.tsx
// Admin-only "Firm administration" page. Top-level takeover view
// (sits alongside Settings / ClientSearch / etc. in App.tsx's
// renderMain). Sibling sections in render order:
//   - Audit Log         (AuditLogPanel below)
//   - Team              (TeamPanel — relocated from Settings)
//   - Deadline Types    (DeadlineTypesPanel — relocated from Settings)
// Future sections (user invites, user removal, firm dashboard)
// land here as additional siblings.
//
// Defence-in-depth on admin gating:
//   1. Sidebar hides the Admin nav item for non-admins.
//   2. This component render-time-gates on authUser.role and
//      shows a small notice instead of the audit panel if a
//      non-admin somehow lands here (stale state, deep-link,
//      future routing changes). Sub-panels rendered inside this
//      page's admin branch DO NOT need their own per-panel gates
//      — the page-level guard is the canonical check.
//
// Layout mirrors Settings: AppHeader + max-w content container.
// Tokens come from the casevo CSS variables so everything themes
// automatically.
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BillWordingPanel from '../components/settings/BillWordingPanel'
import SettlementWordingPanel from '../components/settings/SettlementWordingPanel'
import EsignSettingsPanel from '../components/settings/EsignSettingsPanel'
import { useCasevoStore } from '../store/useCasevoStore'
import { useFirmChange } from '../utils/liveEvents'
import { initialPlaceParam, navigateTo, navigateWithin, usePlaceParam } from '../navigation/navigate'
import { UnsavedGuard, useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import SignatureUploadCard from '../components/settings/SignatureUploadCard'
import FirmSignatureMoveCard, { SignaturePerPersonNote } from '../components/settings/FirmSignatureMoveCard'
import { listAuditLogsApi, listUsersApi, updateUserRoleApi, updateUserBillingGradeApi, updateUserJobTitleApi, deactivateUserApi, reactivateUserApi, adminReset2faApi, adminChangeUserEmailApi, listDeadlineTypesApi, createDeadlineTypeApi, updateDeadlineTypeApi, deleteDeadlineTypeApi, listGradeRatesApi, updateGradeRateApi, repriceActivitiesApi, getBillingSettingsApi, patchBillingSettingsApi, uploadFirmLogoApi, listInvitesApi, createInviteApi, resendInviteApi, revokeInviteApi, getFirmColourSchemeApi, putFirmColourSchemeApi, getCaseHistoryVisibilityApi, putCaseHistoryVisibilityApi, getRequire2faApi, setRequire2faApi, getTaskCompleteNotifyApi, putTaskCompleteNotifyApi } from '../api/casevoApi'
import type { CaseHistoryVisibility, TaskCompleteNotify } from '../api/casevoApi'
import { getUserPermissionsApi, setUserPermissionsApi, getFirmPermissionDefaultsApi, setFirmPermissionDefaultsApi } from '../api/casevoApi'
import { PERMISSION_LABELS, PERMISSION_GROUPS, PER_USER_ONLY_PERMISSIONS } from '../constants/permissions'
import type { PermissionKey, EffectivePermissions, UserPermissionOverrides } from '../constants/permissions'
import { PALETTE, SHIPPED_DEFAULT_SCHEME } from '../constants/palette.constants'
import { ACTION_TYPES } from '../constants/actionTypes.constants'
import { ActionColourAccordion } from '../components/shared/ActionColourAccordion'
import type { AuditLogEntry, Pagination } from '../types/auditLog.types'
import type { FirmUser } from '../types/user.types'
import type { DeadlineType } from '../types/deadlines.types'
import type { GradeRate } from '../types/grades.types'
import type { RepriceResult, FirmBillingSettings, FirmBillingSettingsPatch } from '../api/casevoApi'
import { validateInvoiceNumberFormat, formatInvoiceNumber, buildNumberFormat, parseNumberFormat, prefixError, padError, PREFIX_MAX, type NumberFormatParts } from '../utils/invoiceNumber'
import type { Invite, InviteRole, InviteStatus } from '../types/invite.types'
import { MERGE_FIELD_HELP, resolveMergeFields, sampleMergeValues, unknownMergeFields } from '../utils/billEmailTemplate'
import FormField from '../components/shared/FormField'
import FieldError from '../components/shared/FieldError'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import { SidebarHamburger } from '../components/layout/Sidebar'
import UserAvatar from '../components/shared/UserAvatar'
import ActorAvatar from '../components/shared/ActorAvatar'
import SearchInput from '../components/shared/SearchInput'
import ConfirmDialogue from '../components/shared/ConfirmDialogue'
import { invalidateCurrentGradeRates } from '../components/shared/repriceRule'
import EmailChangeDialog from '../components/shared/EmailChangeDialog'
import { relativeTime } from '../utils/relativeTime'
import { primeFirmLogo, useFirmLogo } from '../utils/firmLogo'
import { accentWarning, documentTheme, normaliseAccent, secondaryWarning } from '../utils/documentTheme'
import { DOCUMENT_FONTS, fontStack } from '../utils/documentFonts'
import { clientDocumentFonts } from '../utils/documentFontFiles'
import { DocumentColourPicker } from '../components/shared/ColourPicker'
import { renderActionSentence } from '../utils/auditSentence'
import {
  SettingsCard, SettingsRow, RowSmall, SettingsToggleRow, SettingsSwitch, SettingsBadge, SettingsSaved, SIcon, sInput,
  S_BTN_PRIMARY, S_BTN_PRIMARY_SM, S_BTN_GHOST, S_BTN_GHOST_SM, S_BTN_DANGER_SM, S_FIELDS,
  S_LABEL, S_NOTE, S_FIELD_ERROR, S_FORMFOOT,
  SectionNavItem,
  SectionNavPill,
} from '../components/settings/settingsUi'
import type { SettingsIconName } from '../components/settings/settingsUi'
import {
  A_SELECT, A_CHIP_BASE, A_CHIP_ON, A_CHIP_OFF, A_CHIP_COUNT, A_TH, A_TD,
  OverrideTag, PermGroupHeading, PermIcon, PERM_ROW, PERM_ROW_FIRST, TristateControl, RadioOptionList, YouTag,
} from '../components/admin/adminUi'
import type { TriState } from '../components/admin/adminUi'
import WarningDayChips from '../components/deadlines/WarningDayChips'
import PiSettlementDefaultsPanel from '../components/admin/PiSettlementDefaultsPanel'
import { ErrorNote } from '../components/shared/ActionBox'

// 40px variant of the admin select (sits beside 40px inputs in forms).
const A_SELECT_40 = A_SELECT.replace('h-[36px]', 'h-[40px]')

// Page size for the audit log. 20 is a comfortable single-screen
// scroll on desktop without overwhelming on mobile.
const PAGE_SIZE = 20

// ── AUDIT LOG PANEL ──────────────────────────────────────────
// Inline sub-component (same pattern as Settings.tsx's TeamPanel
// / DeadlineTypesPanel). Fetches the first page on mount, then
// re-fetches when the user clicks Prev / Next.
//
// State:
//   - entries: AuditLogEntry[] | null     (null = initial loading)
//   - pagination: Pagination | null       (server's pagination metadata)
//   - error: string | null                (last fetch failure)
//   - page: number                        (1-indexed current page)
//   - pageChanging: boolean               (true mid-page-change; dims rows)
//
// Renders the entries via a per-action sentence template (known
// actions get pretty templates; unknown actions fall through to
// a safe generic so the viewer never breaks on new vocabulary).
//
// Takes authUserId as a prop (rather than reading it from the
// store) so it mirrors TeamPanel's pattern — the page-level guard
// in AdminPage already reads authUser once, and threading the id
// down avoids each panel re-subscribing to the store. The id is
// used to mark the current admin's own row in the Actor dropdown
// with a "(you)" hint, same as TeamPanel does in its self-row.
interface AuditLogPanelProps {
  authUserId: string
}
const AuditLogPanel: React.FC<AuditLogPanelProps> = ({ authUserId }) => {
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null)
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState<number>(1)
  const [pageChanging, setPageChanging] = useState<boolean>(false)
  // Actor (user) filter. '' = no filter (= "All users"). The
  // dropdown's <option> values are FirmUser.id strings — passed
  // straight through to listAuditLogsApi's `actorUserId` query
  // param (the empty string is coerced to `undefined` at the call
  // site so no ?actorUserId= is appended). Backend validates that
  // the id belongs to the viewer's firm; a stale/invalid id just
  // returns an empty page rather than a 4xx.
  const [selectedActor, setSelectedActor] = useState<string>('')
  // Category filter — targetType-scoped. '' = no filter (= "All
  // categories"). Dropdown values map 1:1 to backend `targetType`
  // values (task / case / case_deadline). Admin actions
  // (user.role.changed, deadline_type.*) span two targetTypes
  // (`user` + `deadline_type`) and are intentionally NOT exposed
  // as a Category option — they show up under "All categories"
  // and are infrequent enough that no extra filter is warranted.
  // New targetTypes (e.g. `document`) drop in as additional
  // <option>s with no other structural change.
  const [selectedTargetType, setSelectedTargetType] = useState<string>('')
  // Firm users for the Actor dropdown. Fetched once on mount via
  // listUsersApi (admin-only endpoint, same one TeamPanel uses).
  // Sorted alphabetically by display name client-side because
  // backend ordering is unspecified. Defaults to []; if the fetch
  // fails OR the firm somehow has zero users (impossible past the
  // admin guard, since the admin is themselves a user), the array
  // stays empty and the dropdown hides — the Category filter and
  // log itself still work.
  // Actor dropdown users — from the SHARED store slice (loaded once,
  // graceful for non-admins). Sorted by display name (email fallback) to
  // preserve the previous ordering; the small list makes the per-render
  // sort cheap (same philosophy as TeamPanel's filteredUsers).
  const firmUsersRaw = useCasevoStore((s) => s.firmUsers)
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  const firmUsers = [...firmUsersRaw].sort((a, b) => {
    const an = `${a.firstName} ${a.lastName}`.trim() || a.email
    const bn = `${b.firstName} ${b.lastName}`.trim() || b.email
    return an.localeCompare(bn)
  })

  // Fetch on mount + every time `page`, `selectedActor`, OR
  // `selectedTargetType` changes. Cancelled-flag pattern mirrors
  // TeamPanel / DeadlineTypesPanel — a quick Prev/Next or
  // filter-toggle double-click no longer surfaces a stale earlier
  // response.
  useEffect(() => {
    let cancelled = false
    // First load: leave entries=null so the skeleton shows.
    // Subsequent loads: dim the current entries via pageChanging.
    if (entries !== null) setPageChanging(true)
    // Admin viewer fills slots 4 and 6 of listAuditLogsApi
    // (actorUserId, targetType). Slot 3 (action) is intentionally
    // undefined — the per-action "Activity" filter was removed in
    // favour of the broader "Category" (targetType) filter; the
    // param stays in the signature for the backend's sake and for
    // future callers, but the admin viewer doesn't use it. Slot 5
    // (caseId) is the per-case timeline's filter — admin viewer is
    // firm-wide, so undefined here too.
    listAuditLogsApi(
      page,
      PAGE_SIZE,
      undefined,
      selectedActor || undefined,
      undefined,
      selectedTargetType || undefined,
    )
      .then((response) => {
        if (cancelled) return
        setEntries(response.data)
        setPagination(response.pagination)
        setError(null)
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to load audit log'
        setError(message)
      })
      .finally(() => {
        if (cancelled) return
        setPageChanging(false)
      })
    return () => { cancelled = true }
    // entries is intentionally NOT in the deps — including it
    // would cause an infinite loop. The pageChanging branch reads
    // it via state-snapshot, which is fine here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedActor, selectedTargetType])

  // Ensure the shared firm-users slice is loaded (load-once; graceful
  // 403 for non-admins → empty, so the Actor dropdown just hides itself).
  useEffect(() => {
    void loadFirmUsers()
  }, [loadFirmUsers])

  const handlePrev = (): void => {
    if (pagination && page > 1) setPage((p) => p - 1)
  }
  const handleNext = (): void => {
    if (pagination && page < pagination.totalPages) setPage((p) => p + 1)
  }
  // Actor-filter handler. CRITICAL: setSelectedActor AND setPage(1)
  // land in the same React batch → one re-render → one effect
  // re-fire (no double fetch). Without the page reset, switching the
  // filter from "All users" on page 3 to one user with only 1 page
  // of activity would land the admin on a phantom empty page 3 of
  // the filtered set.
  const handleActorChange = (next: string): void => {
    if (next === selectedActor) return
    setSelectedActor(next)
    setPage(1)
  }
  // Category-filter handler — same shape as handleActorChange.
  // setSelectedTargetType + setPage(1) batch into one re-render →
  // one effect re-fire. Page reset is CRITICAL for the same reason:
  // narrowing from "All categories" on page 3 to "Tasks" (which may
  // have 1 page) would otherwise land the admin on a phantom empty
  // page 3 of the filtered set.
  const handleTargetTypeChange = (next: string): void => {
    if (next === selectedTargetType) return
    setSelectedTargetType(next)
    setPage(1)
  }

  return (
    <SettingsCard icon="list" title="Audit log" sub="Every admin-relevant change across the firm. Filter by category or person — newest first.">
      {/* Filters + paging (.tools). Each filter handler resets to page 1. */}
      <div className="flex flex-wrap items-center gap-[10px] pb-[14px]">
        <label htmlFor="audit-category-filter" className="sr-only">Category</label>
        <select
          id="audit-category-filter"
          value={selectedTargetType}
          onChange={(e) => handleTargetTypeChange(e.target.value)}
          className={A_SELECT}
        >
          <option value="">All categories</option>
          <option value="task">Tasks</option>
          <option value="case">Cases</option>
          <option value="case_deadline">Case deadlines</option>
        </select>
        {firmUsers.length > 0 && (
          <>
            <label htmlFor="audit-actor-filter" className="sr-only">Person</label>
            <select
              id="audit-actor-filter"
              value={selectedActor}
              onChange={(e) => handleActorChange(e.target.value)}
              className={A_SELECT}
            >
              <option value="">All users</option>
              {firmUsers.map((u) => {
                const name = `${u.firstName} ${u.lastName}`.trim() || u.email
                const label = u.id === authUserId ? `${name} (you)` : name
                return <option key={u.id} value={u.id}>{label}</option>
              })}
            </select>
          </>
        )}
        <span className="flex-1" />
        {pagination && (
          <>
            <span className={S_NOTE}>
              Page {pagination.page} of {pagination.totalPages}
              {pagination.total > 0 && <> · {pagination.total} total</>}
            </span>
            <button type="button" onClick={handlePrev} disabled={page <= 1 || pageChanging} aria-label="Previous page" className={S_BTN_GHOST_SM}>
              <SIcon name="chev" size={14} className="rotate-180" />
            </button>
            <button type="button" onClick={handleNext} disabled={page >= pagination.totalPages || pageChanging} aria-label="Next page" className={S_BTN_GHOST_SM}>
              <SIcon name="chev" size={14} />
            </button>
          </>
        )}
      </div>
      {error ? (
        <ErrorNote className="mt-[12px]">{error}</ErrorNote>
      ) : entries === null ? (
        <ul aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <li key={i} className="flex items-center gap-[12px] py-[13px] min-h-[54px] border-t border-casevo-border-soft first:border-t-0 animate-pulse">
              <span className="w-[28px] h-[28px] rounded-full bg-casevo-mint/60 shrink-0" />
              <span className="w-3/4 h-[12px] rounded bg-casevo-mint/50" />
            </li>
          ))}
        </ul>
      ) : entries.length === 0 ? (
        <p className={`${S_NOTE} py-[13px]`}>
          {(selectedActor || selectedTargetType) ? 'No entries match these filters.' : 'No audit log entries yet.'}
        </p>
      ) : (
        <ul className={`transition-opacity ${pageChanging ? 'opacity-60' : ''}`}>
          {entries.map((entry) => (
            <li key={entry.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[16px] py-[13px] min-h-[54px] border-t border-casevo-border-soft first:border-t-0">
              <div className="flex gap-[12px] items-center min-w-0">
                <ActorAvatar actorUserId={entry.actorUserId} actorEmail={entry.actorEmail} />
                <span className="text-[13.5px] leading-[1.5] text-casevo-forest min-w-0 break-words">{renderActionSentence(entry)}</span>
              </div>
              <span className={`${S_NOTE} whitespace-nowrap`} title={new Date(entry.createdAt).toLocaleString()}>
                {relativeTime(entry.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SettingsCard>
  )
}

// Sentence renderer extracted to src/utils/auditSentence.tsx (shared
// with CaseHistorySection). The Admin viewer benefits automatically
// from the new task/case/case_deadline templates added there.

// ── TEAM PANEL ───────────────────────────────────────────────
// Admin-only firm team management. Lives inline here (rather than
// extracted) so it sits alongside the other Admin panels in the
// same file. Gated by the parent's authUser?.role === 'admin' check
// (AdminPage's render-time guard) — this component itself assumes
// the caller has already verified the viewer is an admin (it still
// disables the self row defensively).
//
// Behaviour summary:
//   - Loads the firm's users on mount via listUsersApi (loading +
//     error states surfaced inline).
//   - Each row: name + email + role <select>. The self row's select
//     is disabled (defensive against backend's self-demotion 400)
//     plus a "(you)" hint so the disable state isn't mysterious.
//   - Promote (→ admin) fires immediately, no confirm.
//   - Demote (→ user) opens ConfirmDialogue first. The <select> is
//     CONTROLLED by users[i].role so the dropdown stays at 'admin'
//     while the dialogue is open; we only mutate state on confirm.
//     Cancel = nothing happens (dropdown still shows 'admin').
//   - Success: per-row 2-second "Saved ✓" tick + the backend's
//     `note` string in a dismissible banner at the top of the panel
//     (banner persists until the admin × dismisses it — it's an
//     instruction the user must read, not a quick confirmation).
//   - Failure: rollback the optimistic state change + show the
//     backend Error.message in a per-row red inline message.
interface TeamPanelProps {
  authUserId: string
  // "Invite member" in the card header → Admin's Invites section.
  onInvite?: () => void
}
const TeamPanel: React.FC<TeamPanelProps> = ({ authUserId, onInvite }) => {
  // null = loading, [] = empty, [...] = loaded
  const [users, setUsers] = useState<FirmUser[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Per-row UX state. updatingId disables the dropdown during the
  // PATCH round-trip; savedId drives the 2-second "Saved ✓" tick;
  // rowErrors is keyed by userId so one row's failure doesn't clear
  // another's success indicator.
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  // The demote-to-user confirmation. We hold the candidate row so
  // the dialogue can name them and the confirm handler knows what
  // to PATCH. Promote actions don't use this (they fire immediately).
  const [confirmDemote, setConfirmDemote] = useState<FirmUser | null>(null)
  const [confirmDeactivate, setConfirmDeactivate] = useState<FirmUser | null>(null)
  // Admin 2FA reset (lockout recovery) — routes through a confirm dialogue.
  const [confirmReset2fa, setConfirmReset2fa] = useState<FirmUser | null>(null)
  // Card-grid redesign: which member's detail modal is open. Store the ID
  // (not a snapshot) so the modal always reads the LIVE row from `users`,
  // reflecting optimistic role/grade/active updates as they happen.
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
  // Admin "Change email" dialog target (member's LOGIN email).
  const [changeEmailFor, setChangeEmailFor] = useState<FirmUser | null>(null)
  // Bell deep-link (email-change request) → open that member's modal once
  // the list has loaded. One-shot: cleared after use.
  const pendingAdminMemberId = useCasevoStore((s) => s.pendingAdminMemberId)
  // Client-side filter state. statusFilter defaults to 'active' so the
  // panel opens on the "in-use today" cohort (deactivated rows are still
  // accessible via the All toggle or the Deactivated chip). searchQuery
  // is a free-text name-or-email substring filter. Both are inline-applied
  // in `filteredUsers` below — no useMemo (list is small + recompute is
  // cheap; the staleness window from skipping memoisation would be larger
  // than any saved work).
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active')
  const [searchQuery, setSearchQuery] = useState<string>('')
  // The dismissible note banner (the endpoint's `note` field — e.g.
  // "User must log out and back in for the change to take effect").
  // NOT auto-fading: the admin must explicitly dismiss it because
  // it's an instruction, not a confirmation.
  const [noteBanner, setNoteBanner] = useState<string | null>(null)

  // Mount-time fetch. Cancelled flag guards against StrictMode's
  // intentional double-mount and against route-change-mid-fetch.
  useEffect(() => {
    let cancelled = false
    listUsersApi().then((data) => {
      if (!cancelled) setUsers(data)
    }).catch((err) => {
      if (cancelled) return
      const message = err instanceof Error ? err.message : 'Failed to load team'
      setLoadError(message)
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!pendingAdminMemberId) return
    // Team failed to load → drop the one-shot so it can't fire on a LATER
    // unrelated Admin visit.
    if (users === null) {
      if (loadError) useCasevoStore.getState().setPendingAdminMemberId(null)
      return
    }
    const target = users.find((u) => u.id === pendingAdminMemberId)
    if (target) {
      // A deactivated member is hidden by the default 'active' filter.
      if (!target.isActive) setStatusFilter('all')
      setSelectedMemberId(target.id)
    }
    useCasevoStore.getState().setPendingAdminMemberId(null)
  }, [pendingAdminMemberId, users, loadError])

  // ★ Live names: TeamPanel keeps its OWN users list (it also carries
  // role/grade/active edits), so mirror name/avatar changes from the central
  // firmUsers map (patched by the 'user-updated' broadcast / our own save)
  // into it — a member's rename shows here without a reload.
  const centralUsers = useCasevoStore((s) => s.firmUsers)
  useEffect(() => {
    if (centralUsers.length === 0) return
    const byId = new Map(centralUsers.map((u) => [u.id, u]))
    setUsers((list) => {
      if (!list) return list
      let changed = false
      const next = list.map((u) => {
        const c = byId.get(u.id)
        if (!c || (c.firstName === u.firstName && c.lastName === u.lastName && c.avatarColour === u.avatarColour && c.avatarStyle === u.avatarStyle)) return u
        changed = true
        return { ...u, firstName: c.firstName, lastName: c.lastName, avatarColour: c.avatarColour, avatarStyle: c.avatarStyle }
      })
      return changed ? next : list
    })
  }, [centralUsers])

  // Auto-fade for the "Saved ✓" tick — mirrors the 2s fade Settings
  // uses for the name/password save indicators.
  useEffect(() => {
    if (!savedId) return
    const timer = setTimeout(() => setSavedId(null), 2000)
    return () => clearTimeout(timer)
  }, [savedId])

  // Esc closes the member-detail modal (matches the app's modal
  // convention). The confirm dialogues layered on top have their own
  // Cancel buttons.
  useEffect(() => {
    if (!selectedMemberId) return
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setSelectedMemberId(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedMemberId])

  // Optimistic role change with rollback-on-failure. previousRole is
  // captured BEFORE the optimistic update so the rollback path can
  // restore it from the closure (avoids needing to re-read the row
  // from a potentially-already-mutated users array).
  const performRoleChange = async (user: FirmUser, newRole: 'admin' | 'user'): Promise<void> => {
    const previousRole = user.role
    setUpdatingId(user.id)
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[user.id]
      return next
    })
    // Optimistic — apply locally so the dropdown reads the new value
    // immediately. On success we re-sync to whatever the backend echoed.
    setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, role: newRole } : u) : list)
    try {
      const result = await updateUserRoleApi(user.id, newRole)
      // Update IN PLACE — override ONLY the changed field on the existing
      // row. The role endpoint's response is PARTIAL (omits isActive +
      // avatar fields); replacing/spreading it would drop isActive →
      // undefined → the member fails the active-filter and disappears
      // (until a refresh). Overriding only `role` preserves id, name,
      // email, isActive, billingGrade and the avatar fields intact.
      setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, role: newRole } : u) : list)
      setSavedId(user.id)
      if (result.note) setNoteBanner(result.note)
      // Keep the CENTRAL firm list in step: Permissions → By member excludes
      // admins from it, so a promotion/demotion must show there at once.
      void useCasevoStore.getState().loadFirmUsers(true)
    } catch (err) {
      // Roll back the optimistic update.
      setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, role: previousRole } : u) : list)
      const message = err instanceof Error ? err.message : 'Failed to update role'
      setRowErrors((prev) => ({ ...prev, [user.id]: message }))
    } finally {
      setUpdatingId(null)
    }
  }
  // Optimistic billing-grade change with rollback-on-failure. Mirrors
  // performRoleChange exactly (capture previous → setUpdatingId → clear
  // rowError → optimistic local mutation → PATCH → sync on success /
  // rollback on failure), minus the ConfirmDialogue gating. There's no
  // destructive direction for billing grade — A→D and D→A are
  // equivalent in risk — so every change fires immediately.
  const performGradeChange = async (user: FirmUser, newGrade: 'A' | 'B' | 'C' | 'D'): Promise<void> => {
    const previousGrade = user.billingGrade
    setUpdatingId(user.id)
    setRowErrors((prev) => { const next = { ...prev }; delete next[user.id]; return next })
    // Optimistic — apply locally so the dropdown reads the new value immediately.
    setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, billingGrade: newGrade } : u) : list)
    try {
      await updateUserBillingGradeApi(user.id, newGrade)
      // Update IN PLACE — override ONLY billingGrade. The endpoint's
      // response is partial (drops isActive + avatar fields), so using it
      // to replace the row would drop those and the member would fail the
      // active-filter / lose its avatar. Overriding just the changed field
      // preserves everything else.
      setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, billingGrade: newGrade } : u) : list)
      setSavedId(user.id)
    } catch (err) {
      setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, billingGrade: previousGrade } : u) : list)
      const message = err instanceof Error ? err.message : 'Failed to update billing grade'
      setRowErrors((prev) => ({ ...prev, [user.id]: message }))
    } finally {
      setUpdatingId(null)
    }
  }
  // <select> onChange handler. No-op short-circuit on same-value to
  // avoid a pointless round-trip when an admin opens the dropdown and
  // re-selects the current grade. No ConfirmDialogue — see comment
  // on performGradeChange.
  const handleBillingGradeChange = (user: FirmUser, newGrade: 'A' | 'B' | 'C' | 'D'): void => {
    if (newGrade === user.billingGrade) return
    void performGradeChange(user, newGrade)
  }
  // Optimistic active-toggle with rollback. Mirrors performRoleChange's
  // structure exactly — capture previous value, set updatingId, clear
  // any prior row error, optimistic local mutation, PATCH (deactivate
  // OR reactivate depending on the requested next state), sync to
  // backend response on success, rollback + per-row error on failure.
  const performToggleActive = async (user: FirmUser, nextActive: boolean): Promise<void> => {
    const previousActive = user.isActive
    setUpdatingId(user.id)
    setRowErrors((prev) => { const next = { ...prev }; delete next[user.id]; return next })
    setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, isActive: nextActive } : u) : list)
    try {
      if (nextActive) {
        await reactivateUserApi(user.id)
      } else {
        await deactivateUserApi(user.id)
      }
      // Update IN PLACE — override ONLY isActive, preserving id, name,
      // avatar, etc. A deactivated member is NOT lost: it stays in the
      // `users` array (so the detail modal, keyed by id, keeps resolving
      // it) and remains visible under the Deactivated / All filters with
      // the "Deactivated" badge — it only leaves the Active-filtered view
      // by design (that's what the status toggle is for).
      setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, isActive: nextActive } : u) : list)
      setSavedId(user.id)
      // Central list feeds the nav "N active" count + the By-member pills.
      void useCasevoStore.getState().loadFirmUsers(true)
    } catch (err) {
      setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, isActive: previousActive } : u) : list)
      const message = err instanceof Error ? err.message : 'Failed to update status'
      setRowErrors((prev) => ({ ...prev, [user.id]: message }))
    } finally {
      setUpdatingId(null)
    }
  }

  // <select> onChange handler. Promote fires immediately; demote
  // opens the confirm dialogue (no optimistic update — the controlled
  // dropdown reads from users[i].role which we haven't touched, so
  // it visually stays at 'admin' while the dialogue is open).
  const handleRoleChange = (user: FirmUser, newRole: 'admin' | 'user'): void => {
    if (newRole === user.role) return
    if (newRole === 'admin') {
      void performRoleChange(user, 'admin')
    } else {
      setConfirmDemote(user)
    }
  }
  const handleConfirmDemote = (): void => {
    const user = confirmDemote
    if (!user) return
    setConfirmDemote(null)
    void performRoleChange(user, 'user')
  }
  // Active-toggle button handler. Deactivate is destructive (kicks the
  // user out + blocks sign-in) so it routes through a ConfirmDialogue.
  // Reactivate is harmless and fires immediately.
  const handleToggleActive = (user: FirmUser): void => {
    if (user.isActive) setConfirmDeactivate(user)
    else void performToggleActive(user, true)
  }
  const handleConfirmDeactivate = (): void => {
    const user = confirmDeactivate
    if (!user) return
    setConfirmDeactivate(null)
    void performToggleActive(user, false)
  }
  // Admin 2FA reset. Clears the user's 2FA server-side (firm-scoped +
  // admin-gated) so a locked-out user can sign in with password alone and
  // re-enrol. Per-row in-flight + saved-tick, mirroring performToggleActive.
  const performReset2fa = async (user: FirmUser): Promise<void> => {
    setUpdatingId(user.id)
    setRowErrors((prev) => { const next = { ...prev }; delete next[user.id]; return next })
    try {
      await adminReset2faApi(user.id)
      setSavedId(user.id)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset 2FA'
      setRowErrors((prev) => ({ ...prev, [user.id]: message }))
    } finally {
      setUpdatingId(null)
    }
  }
  const handleConfirmReset2fa = (): void => {
    const user = confirmReset2fa
    if (!user) return
    setConfirmReset2fa(null)
    void performReset2fa(user)
  }

  // Derived filtered list. Two predicates AND'd: status (active /
  // inactive / all) then a name-or-email substring match on the
  // trimmed lowercased query. Empty users (null while loading) →
  // empty filter input. Recomputed every render — intentional, see
  // the useState block comment above.
  const filteredUsers = (users ?? []).filter((user) => {
    if (statusFilter === 'active' && !user.isActive) return false
    if (statusFilter === 'inactive' && user.isActive) return false
    const q = searchQuery.trim().toLowerCase()
    if (q) {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
      if (!fullName.includes(q) && !user.email.toLowerCase().includes(q)) return false
    }
    return true
  })

  const statusCounts = {
    active: (users ?? []).filter((u) => u.isActive).length,
    inactive: (users ?? []).filter((u) => !u.isActive).length,
    all: (users ?? []).length,
  }

  return (
    <>
    <SettingsCard
      icon="users"
      title="Members"
      sub="Click a member to change their role, billing grade, login email, access or permissions."
      right={onInvite ? (
        <button type="button" onClick={onInvite} className={S_BTN_PRIMARY_SM}>
          <SIcon name="mail" size={14} />
          Invite member
        </button>
      ) : undefined}
    >
      {/* Search + status chips (.tools). Pure view over the loaded list. */}
      <div className="flex flex-wrap items-center gap-[10px] pb-[14px]">
        <div className="flex-1 min-w-[220px]">
          <SearchInput
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search name or email…"
            ariaLabel="Search team"
            wrapperClassName="w-full"
            className={sInput()}
          />
        </div>
        <div className="flex flex-wrap gap-[6px]" role="group" aria-label="Filter by status">
          {(['active', 'inactive', 'all'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setStatusFilter(opt)}
              aria-pressed={statusFilter === opt}
              className={`${A_CHIP_BASE} ${statusFilter === opt ? A_CHIP_ON : A_CHIP_OFF}`}
            >
              {opt === 'active' ? 'Active' : opt === 'inactive' ? 'Deactivated' : 'All'}
              {users !== null && <span className={A_CHIP_COUNT}>{statusCounts[opt]}</span>}
            </button>
          ))}
        </div>
      </div>
      {/* Dismissible note banner (instruction, not an error — never auto-fades). */}
      {noteBanner && (
        <div className="flex items-start justify-between gap-[12px] mb-[14px] px-[14px] py-[11px] rounded-[10px] bg-casevo-mint border border-casevo-border text-[13px] text-casevo-forest">
          <p className="flex-1 leading-relaxed">{noteBanner}</p>
          <button type="button" onClick={() => setNoteBanner(null)} aria-label="Dismiss notice" className="shrink-0 text-casevo-text-soft hover:text-casevo-forest rounded-sm focus:outline-none casevo-focus">
            <SIcon name="x" size={16} />
          </button>
        </div>
      )}
      {loadError ? (
        <p className="text-[13px] text-casevo-danger py-[13px]">{loadError}</p>
      ) : users === null ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading team…</p>
      ) : filteredUsers.length === 0 ? (
        <p className={`${S_NOTE} py-[13px]`}>
          {searchQuery.trim() || statusFilter !== 'all' ? 'No users match your search.' : 'No users found.'}
        </p>
      ) : (
        // .mgrid — clickable member cards; every control lives in the member panel.
        <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-[12px]">
          {filteredUsers.map((user) => {
            const isSelf = user.id === authUserId
            const isSaved = savedId === user.id
            const rowError = rowErrors[user.id]
            const fullName = `${user.firstName} ${user.lastName}`.trim() || user.email
            return (
              <button
                key={user.id}
                type="button"
                onClick={() => setSelectedMemberId(user.id)}
                aria-label={`Manage ${fullName}`}
                className={`flex gap-[12px] items-center p-[14px] rounded-[12px] border border-casevo-border-soft bg-casevo-surface text-left w-full min-w-0 transition-[border-color,box-shadow] hover:border-casevo-mid hover:shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:hover:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)] focus:outline-none casevo-focus ${user.isActive ? '' : 'opacity-[0.62]'}`}
              >
                <UserAvatar userId={user.id} fallbackName={fullName} fallbackLook={{ colour: user.avatarColour, style: user.avatarStyle, photoUrl: user.avatarPhotoUrl }} size="md" title={false} />
                <div className="min-w-0 flex-1">
                  <b className="block text-[14px] font-semibold text-casevo-forest truncate">
                    {fullName}{isSelf && <YouTag />}
                  </b>
                  <small className="block text-[12.5px] text-casevo-text-soft truncate">{user.email}</small>
                  <div className="flex flex-wrap items-center gap-[6px] mt-[6px]">
                    <SettingsBadge on={user.role === 'admin'}>{user.role === 'admin' ? 'Admin' : 'User'}</SettingsBadge>
                    {user.billingGrade && <SettingsBadge>Grade {user.billingGrade}</SettingsBadge>}
                    {!user.isActive && <SettingsBadge>Deactivated</SettingsBadge>}
                    {isSaved && <SettingsSaved />}
                  </div>
                  {rowError && <p className="text-[12px] text-casevo-danger mt-[5px] truncate" title={rowError}>{rowError}</p>}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </SettingsCard>
      {/* MEMBER PANEL — hero band + account rows + grouped permissions + actions.
          Reads the LIVE row from `users` (optimistic updates reflect at once).
          Self-guards kept: your own role is locked; Deactivate + Reset 2FA are
          hidden on your own row. The confirm dialogues layer on top. */}
      {selectedMemberId && (() => {
        const user = (users ?? []).find((u) => u.id === selectedMemberId)
        if (!user) return null
        const isSelf = user.id === authUserId
        const isUpdating = updatingId === user.id
        const isSaved = savedId === user.id
        const rowError = rowErrors[user.id]
        const fullName = `${user.firstName} ${user.lastName}`.trim() || user.email
        const roleLabel = user.role === 'admin' ? 'Admin' : 'User'
        return (
          <div
            className="fixed inset-0 z-50 overflow-y-auto bg-[rgba(10,20,14,0.45)] flex items-start justify-center px-4 py-[48px]"
            onClick={() => setSelectedMemberId(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`Manage ${fullName}`}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[640px] bg-casevo-surface border border-casevo-border rounded-[16px] shadow-[0_24px_60px_rgba(0,0,0,0.28)] overflow-hidden"
            >
              <div className="bg-[linear-gradient(135deg,#1f4a36,#12261b)] text-white px-[22px] py-[20px] flex gap-[14px] items-center">
                <UserAvatar userId={user.id} fallbackName={fullName} fallbackLook={{ colour: user.avatarColour, style: user.avatarStyle, photoUrl: user.avatarPhotoUrl }} size="lg" title={false} />
                <div className="min-w-0">
                  <b className="block text-[18px] font-semibold truncate">{fullName}{isSelf && <YouTag />}</b>
                  <small className="block text-[13px] text-[#b9e3cc] truncate">
                    {user.email} · {roleLabel}{user.billingGrade ? ` · Grade ${user.billingGrade}` : ''}{user.isActive ? '' : ' · Deactivated'}
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMemberId(null)}
                  aria-label="Close"
                  className="ml-auto w-[32px] h-[32px] rounded-[8px] grid place-items-center text-[#b9e3cc] hover:text-white hover:bg-white/10 shrink-0 focus:outline-none casevo-focus"
                >
                  <SIcon name="x" />
                </button>
              </div>
              <div className="px-[22px] pt-[6px] pb-[18px]">
                <SettingsRow
                  label="Role"
                  action={
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value as 'admin' | 'user')}
                      disabled={isSelf || isUpdating || !user.isActive}
                      aria-label={`Role for ${fullName}`}
                      className={A_SELECT}
                    >
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>
                  }
                >
                  {roleLabel}
                  {isSelf && <RowSmall>You can’t change your own role.</RowSmall>}
                </SettingsRow>
                <SettingsRow
                  label="Billing grade"
                  action={
                    <select
                      value={user.billingGrade ?? 'A'}
                      onChange={(e) => handleBillingGradeChange(user, e.target.value as 'A' | 'B' | 'C' | 'D')}
                      disabled={isUpdating || !user.isActive}
                      aria-label={`Billing grade for ${fullName}`}
                      className={A_SELECT}
                    >
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  }
                >
                  Grade {user.billingGrade ?? '—'}
                </SettingsRow>
                {/* B3 — the position printed when they sign a bill. */}
                <JobTitleRow key={user.id} user={user} fullName={fullName} disabled={isUpdating || !user.isActive}
                  onSaved={(jobTitle) => setUsers((list) => list ? list.map((u) => u.id === user.id ? { ...u, jobTitle } : u) : list)} />
                {/* B3b — their own signature image, printed on bills they sign. */}
                <SignatureUploadCard key={`sig-${user.id}`} userId={user.id} name={fullName} variant="row" disabled={isUpdating || !user.isActive} />
                <SettingsRow
                  label="Login email"
                  action={
                    <button type="button" onClick={() => setChangeEmailFor(user)} disabled={isUpdating} className={S_BTN_GHOST_SM}>
                      Change email
                    </button>
                  }
                >
                  <span className="break-all">{user.email}</span>
                </SettingsRow>
                {/* Per-member permission overrides — grouped by area (Option A). */}
                {user.role === 'admin' ? (
                  <p className={`${S_NOTE} pt-[12px] border-t border-casevo-border-soft`}>
                    Admins always have every permission — per-person overrides apply to non-admins.
                  </p>
                ) : (
                  <div className="border-t border-casevo-border-soft">
                    <UserPermissionsEditor userId={user.id} compact />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-[10px] px-[22px] py-[14px] border-t border-casevo-border-soft bg-casevo-input">
                {!isSelf && (
                  <button
                    type="button"
                    onClick={() => handleToggleActive(user)}
                    disabled={isUpdating}
                    className={user.isActive ? S_BTN_DANGER_SM : S_BTN_GHOST_SM}
                  >
                    {user.isActive ? 'Deactivate user' : 'Reactivate user'}
                  </button>
                )}
                {!isSelf && (
                  <button type="button" onClick={() => setConfirmReset2fa(user)} disabled={isUpdating} className={S_BTN_GHOST_SM}>
                    <SIcon name="lock" size={13} />
                    Reset two-factor auth
                  </button>
                )}
                {isSaved && <SettingsSaved />}
                {rowError && <span className="text-[12.5px] text-casevo-danger" role="alert">{rowError}</span>}
                <span className="flex-1" />
                <button type="button" onClick={() => setSelectedMemberId(null)} className={S_BTN_GHOST_SM}>Close</button>
              </div>
            </div>
          </div>
        )
      })()}
      {/* Demote-to-user confirmation. Promotions skip this and fire
          immediately (low-risk). Cancel just clears confirmDemote —
          the dropdown is controlled by users[i].role which we never
          touched, so it visually stays at 'admin' throughout. */}
      <ConfirmDialogue
        isOpen={confirmDemote !== null}
        title="Remove admin access?"
        message={
          confirmDemote
            ? `${confirmDemote.firstName} ${confirmDemote.lastName} will lose admin access. Continue?`
            : ''
        }
        confirmLabel="Remove admin"
        onConfirm={handleConfirmDemote}
        onCancel={() => setConfirmDemote(null)}
      />
      <ConfirmDialogue
        isOpen={confirmDeactivate !== null}
        title="Deactivate user?"
        message={
          confirmDeactivate
            ? `${confirmDeactivate.firstName} ${confirmDeactivate.lastName} will be logged out and unable to sign in until reactivated. Continue?`
            : ''
        }
        confirmLabel="Deactivate"
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmDeactivate(null)}
      />
      <ConfirmDialogue
        isOpen={confirmReset2fa !== null}
        title="Reset two-factor auth?"
        message={
          confirmReset2fa
            ? `${confirmReset2fa.firstName} ${confirmReset2fa.lastName}'s 2FA will be turned off so they can sign in with their password and set it up again. Continue?`
            : ''
        }
        confirmLabel="Reset 2FA"
        onConfirm={handleConfirmReset2fa}
        onCancel={() => setConfirmReset2fa(null)}
      />
      {/* Admin changes a member's LOGIN email. Backend: PATCH
          /api/users/:id/email (requireAdmin, firm-scoped, global uniqueness,
          revokes the member's sessions, notifies them). */}
      <EmailChangeDialog
        isOpen={changeEmailFor !== null}
        title="Change login email"
        intro={
          changeEmailFor?.id === authUserId
            ? <>This is <strong className="font-semibold text-casevo-forest">your own</strong> login email. You&apos;ll be signed out and must sign in again with the new address.</>
            : <>{changeEmailFor ? `${changeEmailFor.firstName} ${changeEmailFor.lastName}`.trim() || 'This member' : 'This member'} will be signed out on all devices and must sign in with the new address. We&apos;ll email them at both addresses.</>
        }
        currentEmail={changeEmailFor?.email ?? ''}
        submitLabel="Change email"
        busyLabel="Changing…"
        onSubmit={async (newEmail) => {
          const target = changeEmailFor
          if (!target) return
          const res = await adminChangeUserEmailApi(target.id, newEmail)
          setUsers((list) => list ? list.map((u) => u.id === target.id ? { ...u, email: res.email } : u) : list)
          // Refresh the central list — but NOT on a self-change: our own
          // session was just revoked, so a refetch would 401 → auto-logout
          // before the success step shows.
          if (target.id !== authUserId) void useCasevoStore.getState().loadFirmUsers(true)
        }}
        successContent={(newEmail) => changeEmailFor?.id === authUserId
          ? <>Your login email is now <strong className="font-semibold">{newEmail}</strong>. Sign in again with it.</>
          : <>Login email changed to <strong className="font-semibold">{newEmail}</strong>. They&apos;ve been signed out and notified.</>}
        successButtonLabel={changeEmailFor?.id === authUserId ? 'Sign in again' : 'Done'}
        onClose={() => setChangeEmailFor(null)}
        onDone={() => {
          const self = changeEmailFor?.id === authUserId
          setChangeEmailFor(null)
          // Own sessions were revoked server-side → sign out cleanly now.
          if (self) useCasevoStore.getState().clearAuth()
        }}
      />
    </>
  )
}

// ── DEADLINE TYPES PANEL ─────────────────────────────────────
// Admin-only firm-scoped deadline-type catalogue management. Lives
// inline alongside the other Admin panels in this file. Gated
// by the parent's authUser?.role === 'admin' check (AdminPage's
// render-time guard).
//
// Behaviour summary:
//   - Loads the firm's deadline types on mount via
//     listDeadlineTypesApi (loading + error states surfaced inline).
//   - Add form at the top: text input + "+ Add type" button. POSTs
//     via createDeadlineTypeApi; appends to list + re-sorts by name
//     on success; surfaces backend Error.message (e.g. 409 duplicate)
//     inline near the add input on failure.
//   - List rows below. Default view: name + "Edit" link. Only ONE
//     row editable at a time (editingId). Edit mode: inline text
//     input + Save / Cancel + a small muted warning line:
//     "Renaming changes this type on every case that uses it."
//   - Save: if name unchanged, exits edit mode with NO network call.
//     Else PATCH via updateDeadlineTypeApi; on success update local
//     row + re-sort + 2-second "Saved ✓" tick + exit edit mode; on
//     failure surface backend message in a per-row red line and STAY
//     in edit mode so the admin can fix the input.
//   - Cancel: drops editValue + exits edit mode, no call.
//   - Delete (with a confirm) — shown disabled, with the reason as its
//     tooltip, when the backend's deleteBlockedReason says no: the type is
//     in use, or it's the firm's only limitation type.
//   - Limitation types: a rename must keep "limitation" (refused here and
//     by the backend); renaming another type INTO one asks to confirm,
//     since it turns the 30/7/1 floor on.
const DeadlineTypesPanel: React.FC = () => {
  // null = loading, [] = empty, [...] = loaded
  const [types, setTypes] = useState<DeadlineType[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Edit-mode state. Only one row editable at a time. editValue
  // is the in-progress name in the input; persisted locally so the
  // admin can keep typing through React re-renders.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [savingId, setSavingId] = useState<string | null>(null)
  // Per-row UX state. savedId drives the 2-second "Saved ✓" tick.
  // rowErrors is keyed by typeId so one row's failure doesn't clear
  // another's success indicator.
  const [savedId, setSavedId] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  // Add-form state.
  const [addValue, setAddValue] = useState<string>('')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  // Delete-flow state. confirmDelete carries the pending row so the
  // ConfirmDialogue can template its message off the actual name and
  // so cancelling just clears the gate (no rollback needed). Mirrors
  // the TeamPanel.confirmDemote pattern. deletingId disables the
  // row's controls during the in-flight DELETE the same way savingId
  // does during a rename.
  const [confirmDelete, setConfirmDelete] = useState<DeadlineType | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Mount-time fetch. Same cancelled-flag pattern as TeamPanel.
  useEffect(() => {
    let cancelled = false
    listDeadlineTypesApi().then((data) => {
      if (!cancelled) setTypes(sortByName(data))
    }).catch((err) => {
      if (cancelled) return
      const message = err instanceof Error ? err.message : 'Failed to load deadline types'
      setLoadError(message)
    })
    return () => { cancelled = true }
  }, [])

  // Auto-fade for the "Saved ✓" tick — same 2s pattern as TeamPanel.
  useEffect(() => {
    if (!savedId) return
    const timer = setTimeout(() => setSavedId(null), 2000)
    return () => clearTimeout(timer)
  }, [savedId])

  // ── ADD HANDLER ──────────────────────────────────────────
  const handleAdd = async (): Promise<void> => {
    const name = addValue.trim()
    if (!name) return
    setAddError(null)
    setAdding(true)
    try {
      const created = await createDeadlineTypeApi({ name })
      // Append + re-sort. Backend orders alphabetically; local re-sort
      // keeps the list visually consistent without a re-fetch.
      setTypes((list) => list ? sortByName([...list, created]) : [created])
      setAddValue('')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add deadline type'
      setAddError(message)
    } finally {
      setAdding(false)
    }
  }

  // ── ROW EDIT HANDLERS ────────────────────────────────────
  // Round 2 — warning schedule edits save straight away (PATCH warningDays);
  // the backend refuses to drop the limitation floor, and its message shows
  // on the row. `next` = the editable days; the locked floor is re-added.
  const [warnSavingId, setWarnSavingId] = useState<string | null>(null)
  const saveWarningDays = async (type: DeadlineType, next: number[] | 'reset'): Promise<void> => {
    setWarnSavingId(type.id)
    setRowErrors((prev) => { const { [type.id]: _gone, ...rest } = prev; return rest })
    try {
      const updated = await updateDeadlineTypeApi(type.id, next === 'reset'
        ? { resetWarningDays: true }
        : { warningDays: Array.from(new Set([...next, ...type.lockedDays])) })
      setTypes((prev) => (prev ? prev.map((t) => (t.id === type.id ? updated : t)) : prev))
      setSavedId(type.id)
    } catch (err) {
      setRowErrors((prev) => ({ ...prev, [type.id]: err instanceof Error ? err.message : 'Couldn’t save the warnings.' }))
    } finally {
      setWarnSavingId(null)
    }
  }

  const startEdit = (type: DeadlineType): void => {
    setEditingId(type.id)
    setEditValue(type.name)
    // Clear any prior error for this row so it doesn't linger from a
    // previous attempt.
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[type.id]
      return next
    })
  }
  const cancelEdit = (): void => {
    setEditingId(null)
    setEditValue('')
  }
  // Re-read the list after a rename / delete: either can change OTHER rows'
  // deleteBlockedReason (e.g. a second limitation type makes the first
  // deletable; deleting one makes the other the firm's only one).
  const refreshTypes = (): void => {
    listDeadlineTypesApi().then((data) => setTypes(sortByName(data))).catch(() => { /* keep the local list */ })
  }
  // (D12 live updates) Another admin changed the types → the list re-reads
  // (a row being renamed keeps its typed name — editValue is separate).
  useFirmChange(['deadlineTypes'], () => refreshTypes())
  // Renaming another type to include "limitation" turns the floor on — the
  // admin confirms that first (confirmBecomeLim holds the pending rename).
  const [confirmBecomeLim, setConfirmBecomeLim] = useState<{ type: DeadlineType; name: string } | null>(null)
  const saveEdit = async (type: DeadlineType, confirmed = false): Promise<void> => {
    const newName = editValue.trim()
    if (!newName) {
      setRowErrors((prev) => ({ ...prev, [type.id]: 'Name cannot be empty' }))
      return
    }
    // No-op shortcut: if the name hasn't changed, just exit edit mode
    // without firing a network call. Backend handles this as 200 too,
    // but skipping locally avoids the roundtrip.
    if (newName === type.name) {
      cancelEdit()
      return
    }
    // Same rules as the backend (validateTypeRename), checked here first.
    if (LIMITATION_NAME_RE.test(type.name) && !LIMITATION_NAME_RE.test(newName)) {
      setRowErrors((prev) => ({ ...prev, [type.id]: LIMITATION_RENAME_ERROR }))
      return
    }
    if (!confirmed && !LIMITATION_NAME_RE.test(type.name) && LIMITATION_NAME_RE.test(newName)) {
      setConfirmBecomeLim({ type, name: newName })
      return
    }
    setSavingId(type.id)
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[type.id]
      return next
    })
    try {
      const updated = await updateDeadlineTypeApi(type.id, { name: newName })
      // Replace the row in-place + re-sort. Re-sort because alphabetic
      // order may shift after a rename.
      setTypes((list) => list ? sortByName(list.map((t) => t.id === type.id ? updated : t)) : list)
      setSavedId(type.id)
      cancelEdit()
      if (LIMITATION_NAME_RE.test(type.name) !== LIMITATION_NAME_RE.test(newName)) refreshTypes()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update deadline type'
      setRowErrors((prev) => ({ ...prev, [type.id]: message }))
      // Stay in edit mode so the admin can fix the input.
    } finally {
      setSavingId(null)
    }
  }

  // ── DELETE HANDLER ───────────────────────────────────────
  // Fires only after the ConfirmDialogue's "Delete" button is clicked.
  // On success: drop the row + close the modal. On failure (almost
  // always the 409 "in use on N case(s)" path): write the backend
  // message into rowErrors so it surfaces under the row, AND close
  // the modal so the inline error isn't hidden behind it. The row
  // stays put — we only filter on success.
  const handleConfirmDelete = async (): Promise<void> => {
    const type = confirmDelete
    if (!type) return
    setDeletingId(type.id)
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[type.id]
      return next
    })
    try {
      await deleteDeadlineTypeApi(type.id)
      setTypes((list) => list ? list.filter((t) => t.id !== type.id) : list)
      setConfirmDelete(null)
      if (type.isLimitation) refreshTypes()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete deadline type'
      setRowErrors((prev) => ({ ...prev, [type.id]: message }))
      // Close the modal so the inline row error becomes visible.
      setConfirmDelete(null)
    } finally {
      setDeletingId(null)
    }
  }

  // Unsaved: a rename in progress that differs from the saved name, or a
  // typed-but-not-added new type.
  const renameDirty = !!editingId && savingId !== editingId
    && editValue.trim() !== (types?.find((t) => t.id === editingId)?.name ?? '').trim()
  useUnsavedChangesGuard(renameDirty || (addValue.trim() !== '' && !adding))
  return (
    <SettingsCard
      icon="flag"
      title="Deadline types"
      sub="The deadline catalogue used on every case, and when each type warns the people on the case. Renaming changes the type on every case that uses it; a type that’s in use, or the firm’s only limitation type, can’t be deleted."
    >
      <p className={`${S_NOTE} pb-[6px]`}>
        Warnings go to the case’s team (or the firm’s admins if nobody is on the case) as a bell and an email, on each “days before” below.
        Every type also alerts on the due day and on the first overdue day, then appears in one daily “Overdue deadlines” email until it’s completed.
        People can add their own extra reminders in Settings › Notifications, but can’t remove these.
      </p>
      {/* Add form */}
      <div className="flex flex-wrap items-center gap-[10px] pt-[6px] pb-[6px]">
        <input
          type="text"
          value={addValue}
          onChange={(e) => setAddValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd() } }}
          placeholder="e.g. Mediation deadline"
          disabled={adding}
          aria-label="New deadline type name"
          className={`${sInput()} flex-1 min-w-[220px]`}
        />
        <button type="button" onClick={() => { void handleAdd() }} disabled={adding || !addValue.trim()} className={S_BTN_PRIMARY}>
          <SIcon name="plus" size={14} />
          {adding ? 'Adding…' : 'Add type'}
        </button>
      </div>
      {addError && <p className={S_FIELD_ERROR}>{addError}</p>}
      {loadError ? (
        <p className="text-[13px] text-casevo-danger py-[13px]">{loadError}</p>
      ) : types === null ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading deadline types…</p>
      ) : types.length === 0 ? (
        <p className={`${S_NOTE} py-[13px]`}>No deadline types yet.</p>
      ) : (
        <ul className="mt-[8px]">
          {types.map((type) => {
            const isEditing = editingId === type.id
            const isSaving = savingId === type.id
            const isSaved = savedId === type.id
            const isDeleting = deletingId === type.id
            const rowError = rowErrors[type.id]
            return (
              <li key={type.id} className="py-[13px] border-t border-casevo-border-soft first:border-t-0">
                {isEditing ? (
                  <div>
                    <div className="flex flex-wrap items-center gap-[10px]">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); void saveEdit(type) }
                          if (e.key === 'Escape') cancelEdit()
                        }}
                        disabled={isSaving}
                        autoFocus
                        aria-label={`Rename ${type.name}`}
                        className={`${sInput()} flex-1 min-w-[200px]`}
                      />
                      <button type="button" onClick={() => { void saveEdit(type) }} disabled={isSaving || !editValue.trim()} className={S_BTN_PRIMARY_SM}>
                        {isSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button type="button" onClick={cancelEdit} disabled={isSaving} className={S_BTN_GHOST_SM}>Cancel</button>
                    </div>
                    <p className={`${S_NOTE} mt-[6px]`}>
                      Renaming changes this type on every case that uses it.
                      {type.isLimitation && ' Limitation types must keep “limitation” in the name.'}
                    </p>
                    {rowError && <p className={S_FIELD_ERROR}>{rowError}</p>}
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[16px] min-h-[30px]">
                      <div className="flex items-center gap-[10px] min-w-0">
                        <span className="text-[14px] text-casevo-forest truncate">{type.name}</span>
                        {isSaved && <SettingsSaved />}
                      </div>
                      <span className="flex gap-[8px]">
                        <button type="button" onClick={() => startEdit(type)} disabled={isDeleting} className={S_BTN_GHOST_SM}>
                          <SIcon name="pen" size={13} />
                          Rename
                        </button>
                        {/* Blocked (in use / the firm's only limitation type):
                            aria-disabled, not disabled, so the reason still
                            shows as a tooltip and to keyboard + screen-reader users. */}
                        <button type="button"
                          onClick={() => { if (!type.deleteBlockedReason) setConfirmDelete(type) }}
                          disabled={isDeleting}
                          aria-disabled={type.deleteBlockedReason ? true : undefined}
                          title={type.deleteBlockedReason ?? undefined}
                          aria-label={type.deleteBlockedReason ? `Can’t delete ${type.name}: ${type.deleteBlockedReason}` : `Delete ${type.name}`}
                          className={`${S_BTN_DANGER_SM} ${type.deleteBlockedReason ? 'opacity-40 cursor-not-allowed' : ''}`}>
                          <SIcon name="trash" size={13} />
                          {isDeleting ? 'Deleting…' : null}
                        </button>
                      </span>
                    </div>
                    {/* Round 2 — the type's warning schedule */}
                    <div className="mt-[8px]">
                      <div className="flex items-center gap-[10px] mb-[6px]">
                        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-casevo-text-soft">Warnings · days before</span>
                        {type.warningDaysSet
                          ? <button type="button" onClick={() => { void saveWarningDays(type, 'reset') }} disabled={warnSavingId === type.id}
                              className="text-[11.5px] text-casevo-text-soft hover:text-casevo-brand underline underline-offset-2 focus:outline-none casevo-focus disabled:opacity-50">
                              Reset to default ({type.defaultDays.join(', ')})
                            </button>
                          : <span className="text-[11.5px] text-casevo-text-soft">Default</span>}
                        {warnSavingId === type.id && <span className="text-[11.5px] text-casevo-text-soft">Saving…</span>}
                      </div>
                      <WarningDayChips
                        editable={type.warningDays.filter((d) => !type.lockedDays.includes(d))}
                        locked={type.lockedDays}
                        lockedTitle="Required for limitation dates"
                        onChange={(next) => { void saveWarningDays(type, next) }}
                        disabled={warnSavingId === type.id || isDeleting}
                        ariaLabel={`Warnings for ${type.name}`}
                      />
                      {type.isLimitation && (
                        <p className={`${S_NOTE} mt-[6px]`}>
                          <b className="font-semibold">Required for limitation dates:</b> the 30, 7 and 1-day warnings and the overdue alerts can’t be removed, and always email.
                          You can add more.
                        </p>
                      )}
                    </div>
                    {rowError && <p className={S_FIELD_ERROR}>{rowError}</p>}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <ConfirmDialogue
        isOpen={confirmDelete !== null}
        title="Delete deadline type?"
        message={
          confirmDelete
            ? `"${confirmDelete.name}" will be permanently removed. This can't be undone.`
            : ''
        }
        confirmLabel={deletingId ? 'Deleting...' : 'Delete'}
        onConfirm={() => { void handleConfirmDelete() }}
        onCancel={() => setConfirmDelete(null)}
      />
      <ConfirmDialogue
        isOpen={confirmBecomeLim !== null}
        title="Make this a limitation type?"
        message={confirmBecomeLim
          ? `With “limitation” in its name, “${confirmBecomeLim.name}” becomes a limitation type: its 30, 7 and 1-day warnings and overdue alerts become required and can’t be removed, on every case that uses it.`
          : ''}
        confirmLabel="Rename"
        tone="primary"
        onConfirm={() => { const c = confirmBecomeLim; setConfirmBecomeLim(null); if (c) void saveEdit(c.type, true) }}
        onCancel={() => setConfirmBecomeLim(null)}
      />
    </SettingsCard>
  )
}

// Same rule + message as the backend (lib/deadlineWarnings): "limitation"
// is recognised by name, so a limitation type must keep it.
const LIMITATION_NAME_RE = /limitation/i
const LIMITATION_RENAME_ERROR = 'Limitation types must keep ‘limitation’ in the name so their required warnings stay on.'

// Module-scope helper — re-used by both the mount-fetch and every
// state update that adds/renames a row. localeCompare keeps "Trial
// date" before "Trial deadline" the same way the backend's Prisma
// orderBy: { name: 'asc' } does.
const sortByName = (list: DeadlineType[]): DeadlineType[] =>
  [...list].sort((a, b) => a.name.localeCompare(b.name))

// ── GRADE RATES PANEL ────────────────────────────────────────
// Admin-only billing-rates surface. Lives inline alongside the other
// Admin panels in this file. Gated by the parent's authUser?.role ===
// 'admin' check (AdminPage's render-time guard).
//
// Behaviour summary:
//   - Loads the firm's 4 grade rates (A/B/C/D) on mount via
//     listGradeRatesApi. Backend already returns them ordered by
//     grade asc; no client sort needed.
//   - One row per grade. Default mode shows the formatted £ rate +
//     an Edit link + the green Saved tick on success. Edit mode
//     shows a controlled pounds input (e.g. "125.50") + Save + Cancel.
//   - NO add form, NO delete — the 4 rows are seeded at firm creation
//     (firmController.createFirm) and the grade letters are fixed.
//   - POUNDS<->PENCE conversion happens at the edit boundary:
//     display divides by 100 with .toFixed(2); save parses the input
//     as pounds, multiplies by 100, rounds to integer pence. Backend
//     stores hourlyRatePence as a non-negative whole-pence Int.
//   - Per-row UX state mirrors DeadlineTypesPanel exactly: editingId
//     scopes the edit to one row at a time, savingId disables the
//     row during the PATCH, savedId drives the 2s "Saved ✓" tick,
//     rowErrors is keyed by id so one row's failure can't clear
//     another's success indicator.
const GradeRatesPanel: React.FC = () => {
  // Admin's firm list carries billing grades → "Members" column per grade.
  const firmUsersForGrades = useCasevoStore((s) => s.firmUsers)
  // null = loading, [] = empty (defensive — seed guarantees 4 rows), [...] = loaded
  const [rates, setRates] = useState<GradeRate[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Edit-mode state. Only one row editable at a time. editValue is
  // the in-progress POUNDS string (e.g. "125.50") — not pence — so
  // typing feels natural for an admin entering an hourly rate.
  // Conversion happens at the boundary in saveEdit.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [savingId, setSavingId] = useState<string | null>(null)
  // Per-row UX state. savedId drives the 2-second "Saved ✓" tick.
  // rowErrors is keyed by rateId.
  const [savedId, setSavedId] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})

  // Mount-time fetch. Same cancelled-flag pattern as DeadlineTypesPanel.
  useEffect(() => {
    let cancelled = false
    listGradeRatesApi().then((data) => {
      if (!cancelled) setRates(data)
    }).catch((err) => {
      if (cancelled) return
      const message = err instanceof Error ? err.message : 'Failed to load grade rates'
      setLoadError(message)
    })
    return () => { cancelled = true }
  }, [])

  // Auto-fade for the "Saved ✓" tick — same 2s pattern as the other panels.
  useEffect(() => {
    if (!savedId) return
    const timer = setTimeout(() => setSavedId(null), 2000)
    return () => clearTimeout(timer)
  }, [savedId])

  // ── ROW EDIT HANDLERS ────────────────────────────────────
  const startEdit = (rate: GradeRate): void => {
    setEditingId(rate.id)
    // Seed the input with the current rate formatted as pounds.
    // toFixed(2) so a 0-pence row shows "0.00" (not "0"), keeping
    // the field readable when the admin starts typing.
    setEditValue((rate.hourlyRatePence / 100).toFixed(2))
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[rate.id]
      return next
    })
  }
  const cancelEdit = (): void => {
    setEditingId(null)
    setEditValue('')
  }
  // POUNDS<->PENCE CONVERSION lives here. parseFloat tolerates the
  // typical "125.50" form but also "125.5", "125", " 125.50 " (with
  // leading/trailing whitespace), "125.555" (rounded down to 12555
  // via Math.round). It rejects empty strings (NaN), negatives, and
  // non-numeric junk ("abc"). Math.round avoids the classic
  // 1.005 * 100 = 100.49999... drift.
  const saveEdit = async (rate: GradeRate): Promise<void> => {
    const pounds = parseFloat(editValue)
    if (!Number.isFinite(pounds) || pounds < 0) {
      setRowErrors((prev) => ({ ...prev, [rate.id]: 'Enter a valid amount in pounds' }))
      return
    }
    const nextPence = Math.round(pounds * 100)
    // No-op shortcut: if the rate hasn't actually changed, exit edit
    // mode without firing a network call. Saves a round-trip on the
    // common "open editor, change my mind, type the original value
    // back, Save" path.
    if (nextPence === rate.hourlyRatePence) {
      cancelEdit()
      return
    }
    setSavingId(rate.id)
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[rate.id]
      return next
    })
    try {
      const updated = await updateGradeRateApi(rate.id, { hourlyRatePence: nextPence })
      invalidateCurrentGradeRates() // the activity view's Re-price check re-reads the rates
      // Replace the row in-place. Grade letter is fixed so no re-sort.
      setRates((list) => list ? list.map((r) => r.id === rate.id ? updated : r) : list)
      setSavedId(rate.id)
      cancelEdit()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update grade rate'
      setRowErrors((prev) => ({ ...prev, [rate.id]: message }))
      // Stay in edit mode so the admin can fix the input.
    } finally {
      setSavingId(null)
    }
  }

  // Who is on each grade (admin-only list → grades present). Active only.
  const membersFor = (grade: string): string =>
    firmUsersForGrades
      .filter((u) => u.isActive && u.billingGrade === grade)
      .map((u) => `${u.firstName} ${u.lastName}`.trim() || u.email)
      .join(', ')
  // Unsaved: a rate edit in progress whose amount differs from the saved one.
  const editingRate = editingId ? rates?.find((r) => r.id === editingId) : undefined
  useUnsavedChangesGuard(!!editingRate && savingId !== editingId
    && Math.round(parseFloat(editValue) * 100) !== editingRate.hourlyRatePence)
  return (
    <SettingsCard
      icon="pound"
      title="Grade rates"
      sub="Hourly billing rates for grades A–D, used firm-wide. A member’s grade is set on their member panel. New time entries use these rates — existing entries keep the rate they were logged at."
    >
      {loadError ? (
        <p className="text-[13px] text-casevo-danger py-[13px]">{loadError}</p>
      ) : rates === null ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading grade rates…</p>
      ) : rates.length === 0 ? (
        <p className={`${S_NOTE} py-[13px]`}>No grade rates configured.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px] [&_tr:last-child_td]:border-b-0">
            <thead>
              <tr>
                <th className={A_TH}>Grade</th>
                <th className={A_TH}>Hourly rate</th>
                <th className={`${A_TH} hidden sm:table-cell`}>Members</th>
                <th className={A_TH}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rates.map((rate) => {
                const isEditing = editingId === rate.id
                const isSaving = savingId === rate.id
                const isSaved = savedId === rate.id
                const rowError = rowErrors[rate.id]
                return (
                  <tr key={rate.id} className="hover:bg-[#f6faf8] dark:hover:bg-[#2b3b31]">
                    <td className={A_TD}><SettingsBadge on>Grade {rate.grade}</SettingsBadge></td>
                    <td className={A_TD}>
                      {isEditing ? (
                        <div className="flex items-center gap-[8px]">
                          <span className="text-casevo-text-soft" aria-hidden="true">£</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); void saveEdit(rate) }
                              if (e.key === 'Escape') cancelEdit()
                            }}
                            disabled={isSaving}
                            autoFocus
                            aria-label={`Hourly rate in pounds for grade ${rate.grade}`}
                            className={`${sInput()} w-[140px]`}
                          />
                        </div>
                      ) : (
                        <span className="font-mono text-[14px] font-medium text-casevo-forest tabular-nums">
                          £{(rate.hourlyRatePence / 100).toFixed(2)}
                          <span className="font-sans font-normal text-casevo-text-soft ml-[2px]">/h</span>
                        </span>
                      )}
                      {rowError && <p className={S_FIELD_ERROR}>{rowError}</p>}
                    </td>
                    <td className={`${A_TD} hidden sm:table-cell ${S_NOTE}`}>{membersFor(rate.grade) || '—'}</td>
                    <td className={`${A_TD} text-right whitespace-nowrap`}>
                      {isEditing ? (
                        <span className="inline-flex gap-[8px]">
                          <button type="button" onClick={() => { void saveEdit(rate) }} disabled={isSaving || editValue.trim().length === 0} className={S_BTN_PRIMARY_SM}>
                            {isSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" onClick={cancelEdit} disabled={isSaving} className={S_BTN_GHOST_SM}>Cancel</button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-[10px]">
                          {isSaved && <SettingsSaved />}
                          <button type="button" onClick={() => startEdit(rate)} className={S_BTN_GHOST_SM}>
                            <SIcon name="pen" size={13} />
                            Edit
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className={`${S_NOTE} mt-[10px]`}>Enter the rate in pounds (e.g. 125.50). Stored as whole pence. Time is charged in 6-minute units, rounded up — each unit costs a tenth of the hourly rate.</p>
      <RepriceZeroEntries ratesVersion={rates?.map((r) => r.hourlyRatePence).join(',') ?? ''} />
    </SettingsCard>
  )
}

// ── RE-PRICE £0 ENTRIES (inside the Grade rates card) ───────────────────
// A new firm's grade rates start at £0, so every entry logged before the
// admin set them was frozen at £0. This re-prices UNBILLED £0 entries at the
// CURRENT rate for each entry's grade (6-minute units, rounded up) via the
// admin-only POST /api/activities/reprice. Preview (dryRun) first → confirm →
// apply; every changed entry is audit-logged server-side. Billed entries are
// never touched (server-enforced). Lives here because it's the natural next
// step after setting rates; the preview re-runs whenever a rate changes.
const gbp = (p: number): string => `£${(p / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const RepriceZeroEntries: React.FC<{ ratesVersion: string }> = ({ ratesVersion }) => {
  const [preview, setPreview] = useState<RepriceResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<RepriceResult | null>(null)
  const load = useCallback(() => {
    setError(null)
    repriceActivitiesApi({ dryRun: true }).then(setPreview).catch((err) => {
      setError(err instanceof Error ? err.message : 'Could not check for £0 entries')
    })
  }, [])
  useEffect(() => { if (ratesVersion) load() }, [ratesVersion, load])
  const apply = async (): Promise<void> => {
    setConfirming(false)
    setBusy(true)
    setError(null)
    try {
      const result = await repriceActivitiesApi({})
      setDone(result)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Re-pricing failed')
    } finally {
      setBusy(false)
    }
  }
  if (!preview && !error) return null
  const count = preview?.count ?? 0
  const zero = preview?.zeroRateGrades ?? []
  return (
    <div className="mt-[14px] rounded-[10px] border border-casevo-border bg-casevo-input px-[14px] py-[12px] flex flex-wrap items-center gap-x-[14px] gap-y-[8px]">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-casevo-forest">Re-price £0 time entries</div>
        <p className={`${S_NOTE} m-0`}>
          {count > 0
            ? <>{count} unbilled {count === 1 ? 'entry was' : 'entries were'} logged at £0. Re-pricing at today’s rates would charge <b className="font-semibold text-casevo-forest tabular-nums">{gbp(preview!.toPence)}</b>.</>
            : 'No unbilled £0 entries to re-price.'}
          {zero.length > 0 && <> Grade {zero.join(', ')} {zero.length === 1 ? 'is' : 'are'} still £0 — set {zero.length === 1 ? 'its rate' : 'their rates'} first.</>}
          {' '}Billed entries are never changed.
        </p>
        {done && <p className={`${S_NOTE} m-0 mt-[4px] text-casevo-brand dark:text-[#8fd6b0]`}>Re-priced {done.count} {done.count === 1 ? 'entry' : 'entries'} ({gbp(done.toPence)}).{done.skipped ? ` ${done.skipped} changed meanwhile and ${done.skipped === 1 ? 'was' : 'were'} skipped.` : ''}</p>}
        {error && <p className={S_FIELD_ERROR}>{error}</p>}
      </div>
      <button type="button" className={S_BTN_PRIMARY_SM} disabled={busy || count === 0} onClick={() => setConfirming(true)}>
        {busy ? 'Re-pricing…' : 'Re-price…'}
      </button>
      <ConfirmDialogue
        isOpen={confirming}
        title="Re-price £0 entries"
        message={`Re-price ${count} unbilled £0 ${count === 1 ? 'entry' : 'entries'} at the current rate for each entry’s grade (6-minute units, rounded up)? Total: ${gbp(preview?.toPence ?? 0)}. Billed entries are not touched, and every change is recorded in the audit log.`}
        confirmLabel="Re-price entries"
        tone="primary"
        onConfirm={() => { void apply() }}
        onCancel={() => setConfirming(false)}
      />
    </div>
  )
}

// ── BILLING SETTINGS PANEL (Invoicing Stage 0) ───────────────
// Admin-only firm billing configuration: VAT + SRA numbers (printed on
// bills), the bill-numbering template (with a live preview of the NEXT
// number), whether bills need approval before issue, and the default CFA
// success fee %. One Save sends only the changed fields (PATCH
// /api/firm/billing-settings); the server re-validates everything and
// audit-logs the change. The bill-approval switch (top of the card) is the
// exception: it PATCHes on its own the moment it's flipped. ★ Reads NEW Firm columns — ships only after
// Reuben's schema push.
// Invoicing Stage 1 — free-text letterhead / payment fields (server validates).
type BillingTextKey = 'billingAddress' | 'billingPhone' | 'billingEmail' | 'bankAccountName' | 'bankSortCode' | 'bankAccountNumber' | 'complaintsContact'
const BILLING_TEXT_FIELDS: { key: BillingTextKey; label: string; placeholder: string; wide?: boolean; numeric?: boolean }[] = [
  { key: 'billingAddress', label: 'Letterhead address', placeholder: '4 Park Square East\nLeeds LS1 2NE', wide: true },
  { key: 'billingPhone', label: 'Phone', placeholder: '0113 496 0123' },
  { key: 'billingEmail', label: 'Billing email', placeholder: 'accounts@yourfirm.co.uk' },
  { key: 'bankAccountName', label: 'Bank account name', placeholder: 'Your Firm LLP Office Account' },
  { key: 'bankSortCode', label: 'Sort code', placeholder: '20-45-77', numeric: true },
  { key: 'bankAccountNumber', label: 'Account number', placeholder: '12345678', numeric: true },
  { key: 'complaintsContact', label: 'Complaints contact (printed in the rights notice)', placeholder: 'our complaints partner is … (complaints@yourfirm.co.uk)', wide: true },
]

// Letterhead branding — the firm LOGO, the DOCUMENT COLOUR, the optional
// SECONDARY COLOUR and the document FONTS, with a live preview of a document
// header + table in them. Printed on every firm PDF / document (bills, credit
// notes, statements, case summaries); the app itself stays Casevo-branded.
//
// ★ Nothing here saves on its own: all of it is part of the Admin › Branding
// form (BrandingSettingsPanel) and
// is applied by "Save branding" (and counts as its unsaved changes).
//   • Logo — the bytes go THROUGH the server (uploadFirmLogoApi), which checks
//     them by content (PNG / JPEG only — no SVG — 1 MB) and stores each upload
//     under a NEW key, then returns its id. The firm's logo does NOT change:
//     the id is held here as a PENDING logo (previewed) until Save sends it
//     (the server re-checks it's this firm's). Remove is pending the same way.
//     A discarded upload just stays unused; files are never deleted (issued
//     bills keep the logo they were issued with).
//   • Colours — the shared colour picker (components/shared/ColourPicker:
//     colour area, hue + eyedropper, hex, the app-wide Recent colours); the
//     preview follows the drag live. Text on a colour is picked for readability automatically
//     (utils/documentTheme); a colour too light to read as text on white gets
//     a warning and a darker suggestion, and the documents print their
//     headings in that darker shade regardless. The secondary colour (pills,
//     table headers, rules, balance box) is optional — Clear = derived from
//     the document colour — and is flagged when it's too close to tell apart.
//   • Fonts — utils/documentFonts (bundled OFL fonts; each named in its own
//     face in the dropdown). Default = DM Sans, what the documents always used.
const MAX_LOGO_BYTES = 1024 * 1024

// Every document font's faces, for the dropdown + preview (the browser only
// downloads a file once something on the page uses it).
const ALL_DOCUMENT_FONT_FACES = (): string => DOCUMENT_FONTS.map((f) => clientDocumentFonts(f.id, f.id).faces).join('\n')

// A font dropdown whose options are each shown IN their font (a native
// <select> can't style its options everywhere). Button + listbox: ↑/↓ /
// Home / End move, Enter / Space pick, Escape / Tab close.
const FontSelect: React.FC<{ id: string; labelId: string; value: string; defaultId: string; onChange: (id: string) => void }> = ({ id, labelId, value, defaultId, onChange }) => {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const current = DOCUMENT_FONTS.find((f) => f.id === value) ?? DOCUMENT_FONTS[0]
  useEffect(() => {
    if (!open) return
    listRef.current?.focus()
    const onDown = (e: MouseEvent): void => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])
  const openList = (): void => { setActive(Math.max(0, DOCUMENT_FONTS.findIndex((f) => f.id === current.id))); setOpen(true) }
  const pick = (i: number): void => { onChange(DOCUMENT_FONTS[i].id); setOpen(false); btnRef.current?.focus() }
  const onListKey = (e: React.KeyboardEvent): void => {
    const n = DOCUMENT_FONTS.length
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(n - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)) }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0) }
    else if (e.key === 'End') { e.preventDefault(); setActive(n - 1) }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(active) }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); btnRef.current?.focus() }
    else if (e.key === 'Tab') setOpen(false)
  }
  const kind = (k: 'sans' | 'serif'): string => (k === 'serif' ? 'Serif' : 'Sans')
  return (
    <div className="relative" ref={wrapRef}>
      <button ref={btnRef} id={id} type="button" className={`${sInput(false)} w-full flex items-center justify-between gap-[8px] text-left`}
        aria-haspopup="listbox" aria-expanded={open} aria-labelledby={`${labelId} ${id}`}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openList() } }}>
        <span className="truncate text-[14.5px]" style={{ fontFamily: fontStack(current.id) }}>{current.name}</span>
        <span className="text-[11px] text-casevo-text-soft shrink-0">{kind(current.kind)}{current.id === defaultId ? ' · default' : ''} ▾</span>
      </button>
      {open && (
        <ul ref={listRef} role="listbox" tabIndex={-1} aria-labelledby={labelId} aria-activedescendant={`${id}-opt-${active}`} onKeyDown={onListKey}
          className="absolute z-20 mt-[4px] left-0 right-0 max-h-[280px] overflow-auto rounded-[8px] border border-casevo-border bg-casevo-surface shadow-lg py-[4px] focus:outline-none">
          {DOCUMENT_FONTS.map((f, i) => (
            <li key={f.id} id={`${id}-opt-${i}`} role="option" aria-selected={f.id === current.id}
              onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(i)}
              className={`flex items-center justify-between gap-[8px] px-[12px] py-[7px] cursor-pointer ${i === active ? 'bg-casevo-mint/40' : ''}`}>
              <span className="text-[15px] text-casevo-forest" style={{ fontFamily: fontStack(f.id) }}>{f.name}</span>
              <span className="text-[11px] text-casevo-text-soft shrink-0">{f.id === current.id ? '✓ ' : ''}{kind(f.kind)}{f.id === defaultId ? ' · default' : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const LetterheadBranding: React.FC<{
  firmName: string
  address: string | null
  logoId: string | null       // the form's (possibly pending) logo
  savedLogoId: string | null  // the firm's current logo
  accent: string              // the form's colour text ("#2d6a4f")
  defaultAccent: string
  secondary: string           // the form's secondary colour text ('' = none)
  headingFont: string         // DOCUMENT_FONTS ids
  bodyFont: string
  defaultFont: string
  logoError?: string
  accentError?: string | null
  secondaryError?: string | null
  onLogo: (logoId: string | null) => void
  onAccent: (v: string) => void
  onSecondary: (v: string) => void
  onHeadingFont: (id: string) => void
  onBodyFont: (id: string) => void
}> = ({ firmName, address, logoId, savedLogoId, accent, defaultAccent, secondary, headingFont, bodyFont, defaultFont, logoError, accentError, secondaryError, onLogo, onAccent, onSecondary, onHeadingFont, onBodyFont }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const logo = useFirmLogo(logoId)
  const faces = useMemo(ALL_DOCUMENT_FONT_FACES, [])
  const onFile = async (file: File | undefined): Promise<void> => {
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') { setErr('Choose a PNG or JPG image (SVG isn’t supported).'); return }
    if (file.size > MAX_LOGO_BYTES) { setErr('That image is too large (max 1 MB).'); return }
    setBusy(true); setErr(null)
    try {
      const up = await uploadFirmLogoApi(file)
      primeFirmLogo(up.logoId, up.dataUri)
      onLogo(up.logoId)
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not upload the logo. Please try again.') } finally { setBusy(false) }
  }
  const has = !!logoId
  const logoPending = logoId !== savedLogoId
  const valid = normaliseAccent(accent)
  const validSecondary = normaliseAccent(secondary)
  const th = documentTheme(valid ?? defaultAccent, validSecondary)
  const warn = valid ? accentWarning(valid) : null
  const secWarn = validSecondary ? secondaryWarning(valid ?? defaultAccent, validSecondary) : null
  const headFace = fontStack(headingFont)
  const bodyFace = fontStack(bodyFont)
  const ink = '#1a2420'
  const mute = '#7a8880'
  return (
    <div className="sm:col-span-2">
      {/* The document fonts' @font-face rules (bundled files — utils/documentFonts). */}
      <style>{faces}</style>
      {/* Live preview — the PDFs' letterhead (.lh), title band (.band + pills)
          and a table (.sec-h + header row + rows + .bal), on white paper, in
          the chosen colours and fonts. */}
      <span className={S_LABEL}>Document preview</span>
      <div className="rounded-[10px] border border-casevo-border bg-white px-[18px] py-[14px] overflow-hidden" style={{ fontFamily: bodyFace }} aria-label="Document preview in the chosen logo, colours and fonts">
        <div className="flex items-center gap-[11px] pb-[10px]" style={{ borderBottom: `2px solid ${th.rule}` }}>
          {has && logo
            ? <img src={logo} alt="Firm logo" className="block shrink-0 max-h-[45px] max-w-[170px] w-auto h-auto object-contain" />
            : <div className="grid place-items-center shrink-0 w-[33px] h-[33px] rounded-[8px] font-extrabold text-[15px]" style={{ background: `linear-gradient(135deg, ${th.bandFrom}, ${th.bandTo})`, color: th.onAccent, fontFamily: headFace }}>{(firmName || 'C').trim()[0]?.toUpperCase() ?? 'C'}</div>}
          <div className="min-w-0 font-extrabold text-[16px] leading-[1.15] truncate" style={{ color: th.firmInk, fontFamily: headFace }}>
            {firmName || 'Your firm'}
            <small className="block font-medium text-[8px] tracking-[0.12em] uppercase mt-[2px]" style={{ color: mute, fontFamily: bodyFace }}>Solicitors</small>
          </div>
          <div className="ml-auto text-right text-[9px] leading-[1.5] whitespace-pre-line hidden sm:block" style={{ color: '#55635c' }}>{address || 'Letterhead address'}</div>
        </div>
        {has && !logo && <p className="text-[11.5px] mt-[8px] mb-0" style={{ color: mute }}>Loading the logo…</p>}
        <div className="mt-[10px] rounded-[9px] px-[14px] py-[11px]" style={{ background: `linear-gradient(135deg, ${th.bandFrom}, ${th.bandTo})`, color: th.onAccent }}>
          <div className="flex gap-[5px] mb-[4px]">
            {['Interim statute bill', 'INV-0047'].map((p, i) => <span key={p} className={`text-[8px] font-bold rounded-[3px] px-[5px] py-[1px] ${i ? 'tabular-nums tracking-[0.02em]' : 'tracking-[0.1em] uppercase'}`} style={{ background: th.pillBg, color: th.pillInk }}>{p}</span>)}
          </div>
          <div className="text-[17px] font-bold leading-[1.2]" style={{ fontFamily: headFace }}>Invoice</div>
          <div className="text-[10px] mt-[1px]" style={{ color: `rgba(${th.onAccentRgb},.85)` }}>Smith v ABC Insurance · our ref SMI-0012</div>
        </div>
        <div className="mt-[12px] flex items-center gap-[6px] text-[9px] font-bold tracking-[0.16em] uppercase pb-[4px] mb-[4px]" style={{ color: th.heading, borderBottom: `1px solid ${th.secRule}`, fontFamily: headFace }}>
          <i className="inline-block w-[3px] h-[10px] rounded-[2px]" style={{ background: th.accent }} />Profit costs
        </div>
        <table className="w-full border-collapse text-[11px]" style={{ color: ink }}>
          <thead><tr>{['Work done', 'Units', 'Amount'].map((h, i) => <th key={h} className={`text-[8px] tracking-[0.14em] uppercase font-bold py-[3px] px-[4px] ${i ? 'text-right' : 'text-left'}`} style={{ color: th.headInk, background: th.headBg, borderBottom: `1px solid ${th.headRule}` }}>{h}</th>)}</tr></thead>
          <tbody>
            {[['Initial client meeting', '8', '£176.00'], ['Letter of claim to insurer', '5', '£110.00']].map((r) => (
              <tr key={r[0]}>{r.map((c, i) => <td key={i} className={`py-[3px] px-[4px] ${i ? 'text-right tabular-nums' : ''}`} style={{ borderBottom: `1px dotted #e3e9e6` }}>{c}</td>)}</tr>
            ))}
          </tbody>
        </table>
        <div className="mt-[8px] ml-auto max-w-[220px] rounded-[7px] px-[11px] py-[6px] flex justify-between items-center" style={{ background: `linear-gradient(135deg, ${th.balFrom}, ${th.balTo})`, color: th.onBal }}>
          <span className="text-[8px] tracking-[0.14em] uppercase font-bold opacity-80">Balance due</span><span className="text-[14px] font-bold tabular-nums">£343.20</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px] mt-[14px]">
        <div>
          <span className={S_LABEL}>Logo <span className="normal-case tracking-normal font-normal text-casevo-text-soft">(optional)</span></span>
          <div className="flex flex-wrap gap-[8px]">
            <input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" aria-label="Upload firm logo" onChange={(e) => { void onFile(e.target.files?.[0]) }} />
            <button type="button" className={S_BTN_GHOST} disabled={busy} onClick={() => inputRef.current?.click()}>
              {busy ? 'Uploading…' : has ? 'Replace logo' : 'Upload logo'}
            </button>
            {has && <button type="button" className={`${S_BTN_GHOST} !text-casevo-danger`} disabled={busy} onClick={() => { setErr(null); onLogo(null) }}>Remove</button>}
            {logoPending && savedLogoId !== null && <button type="button" className={S_BTN_GHOST} disabled={busy} onClick={() => { setErr(null); onLogo(savedLogoId) }}>Keep current logo</button>}
          </div>
          {err || logoError
            ? <p className={S_FIELD_ERROR} role="alert">{err || logoError}</p>
            : <p className={`${S_NOTE} mt-[5px]`}>
                {logoPending && <b className="text-casevo-forest">{has ? 'New logo — ' : 'Logo removed — '}not saved yet. </b>}
                PNG or JPG, up to 1 MB — a wide logo on a white or transparent background works best. Printed up to about 60px tall, never stretched. Applied when you save; bills already issued keep the logo they were issued with.
              </p>}
        </div>
        <div>
          <label className={S_LABEL} htmlFor="bill-accent">Document colour</label>
          <div className="flex items-center gap-[8px]">
            <DocumentColourPicker id="bill-accent" label="Document colour" value={accent} fallback={defaultAccent} onChange={onAccent}
              emptyText={defaultAccent.toUpperCase()} invalid={!!accentError} describedBy="bill-accent-help" />
            {valid !== defaultAccent && <button type="button" className={S_BTN_GHOST} onClick={() => onAccent(defaultAccent)}>Casevo green</button>}
          </div>
          {accentError
            ? <p id="bill-accent-help" className={S_FIELD_ERROR} role="alert">{accentError}</p>
            : warn && !warn.ok && warn.suggestion
              ? <p id="bill-accent-help" className="mt-[6px] rounded-[8px] border border-[#efe4c4] bg-[#fbf6e8] dark:bg-[#3a3220] dark:border-[#5c4c26] text-[#5c4a12] dark:text-[#f1d9a6] px-[10px] py-[7px] text-[12.5px] leading-[1.5]" role="status">
                  <b>Too light to read as text on white paper.</b> Headings will print in a darker shade, <span className="font-mono">{warn.suggestion.toUpperCase()}</span>, so documents stay readable.{' '}
                  <button type="button" className="underline font-semibold" onClick={() => onAccent(warn.suggestion as string)}>Use {warn.suggestion.toUpperCase()}</button>
                </p>
              : <p id="bill-accent-help" className={`${S_NOTE} mt-[5px]`}>The title band, headings and balance on every document. Text on the colour is set to white or near-black automatically. Documents only — the app stays Casevo green.</p>}
        </div>
        <div>
          <label className={S_LABEL} htmlFor="bill-secondary">Secondary colour <span className="normal-case tracking-normal font-normal text-casevo-text-soft">(optional)</span></label>
          <div className="flex items-center gap-[8px]">
            <DocumentColourPicker id="bill-secondary" label="Secondary colour" value={secondary} fallback={valid ?? defaultAccent} onChange={onSecondary}
              emptyText="None" invalid={!!secondaryError} describedBy="bill-secondary-help" />
            {secondary.trim() !== '' && <button type="button" className={S_BTN_GHOST} onClick={() => onSecondary('')}>Clear</button>}
          </div>
          {secondaryError
            ? <p id="bill-secondary-help" className={S_FIELD_ERROR} role="alert">{secondaryError}</p>
            : secWarn?.similar
              ? <p id="bill-secondary-help" className="mt-[6px] rounded-[8px] border border-[#efe4c4] bg-[#fbf6e8] dark:bg-[#3a3220] dark:border-[#5c4c26] text-[#5c4a12] dark:text-[#f1d9a6] px-[10px] py-[7px] text-[12.5px] leading-[1.5]" role="status">
                  <b>Too close to the document colour to tell apart.</b> Pick a more distinct shade — or Clear it to use shades of the document colour.
                </p>
              : <p id="bill-secondary-help" className={`${S_NOTE} mt-[5px]`}>
                  {validSecondary
                    ? <>The pills on the title band, table headers, divider rules and the balance box.{secWarn?.adjusted ? ' The pills use a lighter or darker shade of it so they stand out from the band and stay readable.' : ''}</>
                    : 'None — the pills, table headers, divider rules and balance box use shades of the document colour. Pick one to set them apart.'}
                </p>}
        </div>
        <div className="grid grid-cols-1 gap-[10px]">
          <div>
            <span className={S_LABEL} id="bill-heading-font-label">Heading font</span>
            <FontSelect id="bill-heading-font" labelId="bill-heading-font-label" value={headingFont} defaultId={defaultFont} onChange={onHeadingFont} />
          </div>
          <div>
            <span className={S_LABEL} id="bill-body-font-label">Body font</span>
            <FontSelect id="bill-body-font" labelId="bill-body-font-label" value={bodyFont} defaultId={defaultFont} onChange={onBodyFont} />
            <p className={`${S_NOTE} mt-[5px]`}>Headings: the firm name, document title and section headings. Body: everything else. Bundled with Casevo, so emailed and printed copies match.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
// UK bank details — typed values are normalised as you type, matching what the
// server stores (firmSettingsController: sort code → "XX-XX-XX", account
// number → 8 digits). Both are idempotent, so saved values load unchanged.
const formatSortCode = (v: string): string => (v.replace(/\D/g, '').slice(0, 6).match(/.{1,2}/g) ?? []).join('-')
const formatAccountNumber = (v: string): string => v.replace(/\D/g, '').slice(0, 8)
const normaliseBillingText = (key: BillingTextKey, v: string): string =>
  key === 'bankSortCode' ? formatSortCode(v) : key === 'bankAccountNumber' ? formatAccountNumber(v) : v

// UK VAT number → "GB" + 9 digits (or 12 for a branch trader), or null if it
// isn't one. Spaces are ignored and "GB" is optional ("123 4567 89",
// "gb123456789" → "GB123456789"). Same rule as the server's normaliseVat.
const vatCanonical = (v: string): string | null => {
  const s = v.replace(/\s+/g, '').toUpperCase()
  const body = s.startsWith('GB') ? s.slice(2) : s
  return /^(\d{9}|\d{12})$/.test(body) ? `GB${body}` : null
}
// Live display format, like the sort code's dashes: digits (with or without a
// typed "GB") become "GB 123 4567 89", and a branch number "GB 123 4567 89 012".
// Anything else (XI…, GBGD…, stray letters) is left as typed so validation can
// flag it rather than silently rewriting it into a GB number.
const formatVatInput = (v: string): string => {
  const s = v.toUpperCase().replace(/[\s-]/g, '')
  if (!/^(G|GB)?\d*$/.test(s)) return v.toUpperCase()
  const d = s.replace(/^GB?/, '').slice(0, 12)
  if (!d) return s
  return `GB ${[d.slice(0, 3), d.slice(3, 7), d.slice(7, 9), d.slice(9, 12)].filter(Boolean).join(' ')}`
}

const emptyExtra = (): Record<BillingTextKey, string> => Object.fromEntries(BILLING_TEXT_FIELDS.map((f) => [f.key, ''])) as Record<BillingTextKey, string>

// ── Bill / credit-note numbering: plain-English builder ──────
// Solicitors never see {SEQ}/{YYYY} tokens: the controls below assemble the
// same stored format string (utils/invoiceNumber buildNumberFormat) and the
// server validates + numbers from it exactly as before. A stored format the
// builder can't represent opens in a small "advanced" raw field instead of
// being lost.
interface NumberingState { advanced: boolean; parts: NumberFormatParts; raw: string; start: string }
const numberingFromStored = (stored: string | null, fallback: string, next: number): NumberingState => {
  const fmt = stored ?? fallback
  const parsed = parseNumberFormat(fmt)
  return { advanced: !parsed, parts: parsed ?? parseNumberFormat(fallback) ?? { prefix: '', year: 'YYYY', month: false, pad: 4 }, raw: fmt, start: String(next) }
}
const numberingFormat = (s: NumberingState): string => (s.advanced ? s.raw.trim() : buildNumberFormat(s.parts))
const numberingFormatError = (s: NumberingState): string | null =>
  s.advanced ? validateInvoiceNumberFormat(s.raw.trim()) : prefixError(s.parts.prefix) ?? padError(s.parts.pad)
/** Start-from validation (mirrors patchBillingSettings): 1…99,999,999; once any are issued, never below the current position. */
const numberingStartError = (s: NumberingState, next: number, issued: number, noun: string, nouns: string): string | null => {
  if (!/^\d{1,8}$/.test(s.start.trim()) || Number(s.start) < 1) return 'Enter a whole number from 1 to 99,999,999.'
  if (issued > 0 && Number(s.start) < next) return `Your ${nouns} have already reached number ${next - 1}, so the next ${noun} must be ${next} or higher. Numbering can move forward, but never back.`
  return null
}
const PAD_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8]
const padLabel = (n: number): string => (n === 0 ? 'No fixed length (1, 2, 3 …)' : `${n} digit${n === 1 ? '' : 's'} (${String(1).padStart(n, '0')})`)

const NumberingBuilder: React.FC<{
  id: string; title: string; noun: string; nouns: string; examplePrefix: string
  state: NumberingState; onChange: (s: NumberingState) => void; issued: number; next: number
  formatError: string | null; startError: string | null; clash: string | null; serverFormatError?: string; serverStartError?: string
  preview: string | null; resetParts: NumberFormatParts
}> = ({ id, title, noun, nouns, examplePrefix, state, onChange, issued, next, formatError, startError, clash, serverFormatError, serverStartError, preview, resetParts }) => {
  const set = (p: Partial<NumberFormatParts>) => onChange({ ...state, parts: { ...state.parts, ...p } })
  const fmtErr = formatError || clash || serverFormatError
  const stErr = startError || serverStartError
  const check = 'h-4 w-4 rounded accent-casevo-brand cursor-pointer'
  return (
    <fieldset className="min-w-0 rounded-[12px] border border-casevo-border-soft p-[14px] m-0" aria-describedby={`${id}-preview`}>
      <legend className="px-[4px] text-[13.5px] font-semibold text-casevo-forest">{title}</legend>
      {state.advanced ? (
        <div className="mt-[4px]">
          <label className={S_LABEL} htmlFor={`${id}-raw`}>Custom pattern</label>
          <input id={`${id}-raw`} className={`${sInput(!!fmtErr)} font-mono`} value={state.raw} onChange={(e) => onChange({ ...state, raw: e.target.value })} autoComplete="off" aria-invalid={!!fmtErr || undefined} />
          {fmtErr ? <p className={S_FIELD_ERROR}>{fmtErr}</p>
            : <p className={`${S_NOTE} mt-[5px]`}>Your saved pattern is more detailed than the simple options can show, so it’s kept as it is.</p>}
          <button type="button" className={`${S_BTN_GHOST_SM} mt-[6px]`} onClick={() => onChange({ ...state, advanced: false, parts: resetParts })}>Use the simple options instead</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px] mt-[4px]">
          <div className="min-w-0">
            <label className={S_LABEL} htmlFor={`${id}-prefix`}>Prefix</label>
            <input id={`${id}-prefix`} className={sInput(!!(formatError || serverFormatError))} value={state.parts.prefix} maxLength={PREFIX_MAX + 4}
              onChange={(e) => set({ prefix: e.target.value.toUpperCase() })} placeholder={`e.g. ${examplePrefix}`} autoComplete="off"
              aria-invalid={!!(formatError || serverFormatError) || undefined} aria-describedby={`${id}-prefix-help`} />
            {(formatError || serverFormatError)
              ? <p id={`${id}-prefix-help`} className={S_FIELD_ERROR}>{formatError || serverFormatError}</p>
              : <p id={`${id}-prefix-help`} className={`${S_NOTE} mt-[5px]`}>Letters and numbers, up to {PREFIX_MAX}. Can be left blank.</p>}
          </div>
          <div className="min-w-0">
            <label className={S_LABEL} htmlFor={`${id}-pad`}>Number length</label>
            <select id={`${id}-pad`} className={sInput(false)} value={state.parts.pad} onChange={(e) => set({ pad: Number(e.target.value) })}>
              {PAD_OPTIONS.map((n) => <option key={n} value={n}>{padLabel(n)}</option>)}
            </select>
            <p className={`${S_NOTE} mt-[5px]`}>Shorter numbers are padded with zeros.</p>
          </div>
          <div className="sm:col-span-2 flex flex-wrap items-center gap-x-[18px] gap-y-[8px]">
            <label className="inline-flex items-center gap-[8px] text-[13.5px] text-casevo-forest cursor-pointer">
              <input type="checkbox" className={check} checked={state.parts.year !== 'none'} onChange={(e) => set({ year: e.target.checked ? 'YYYY' : 'none' })} />Include the year
            </label>
            {state.parts.year !== 'none' && (
              <select aria-label="Year style" className={`${sInput(false)} !w-auto !h-[30px] !py-0 text-[13px]`} value={state.parts.year} onChange={(e) => set({ year: e.target.value as 'YYYY' | 'YY' })}>
                <option value="YYYY">{new Date().getUTCFullYear()}</option><option value="YY">{String(new Date().getUTCFullYear()).slice(-2)}</option>
              </select>
            )}
            <label className="inline-flex items-center gap-[8px] text-[13.5px] text-casevo-forest cursor-pointer">
              <input type="checkbox" className={check} checked={state.parts.month} onChange={(e) => set({ month: e.target.checked })} />Include the month
            </label>
          </div>
          <p className={`${S_NOTE} sm:col-span-2 m-0`}>A running number is always added at the end — it’s what keeps every {noun} number unique.</p>
          {clash && <p className={`${S_FIELD_ERROR} sm:col-span-2 m-0`} role="alert">{clash}</p>}
        </div>
      )}
      <div className="mt-[12px] min-w-0">
        <label className={S_LABEL} htmlFor={`${id}-start`}>Start numbering from</label>
        {issued > 0 && (
          <p className={`${S_NOTE} mb-[6px]`}>
            {issued} {issued === 1 ? noun : nouns} issued so far. The last number used was {next - 1}; the next will be <b className="text-casevo-forest">{next}</b>. You can move this forward, but never back, so no number is ever reused.
          </p>
        )}
        <input id={`${id}-start`} className={`${sInput(!!stErr)} w-[140px] tabular-nums`} value={state.start} inputMode="numeric" maxLength={8}
          onChange={(e) => onChange({ ...state, start: e.target.value.replace(/\D/g, '') })} aria-invalid={!!stErr || undefined} aria-describedby={`${id}-start-help`} />
        {stErr
          ? <p id={`${id}-start-help`} className={S_FIELD_ERROR}>{stErr}</p>
          : <p id={`${id}-start-help`} className={`${S_NOTE} mt-[5px]`}>{issued > 0 ? `Leave it at ${next} to carry straight on.` : `No ${nouns} issued yet — set this to carry on from your old system (e.g. 1047).`}</p>}
      </div>
      <p id={`${id}-preview`} className="mt-[12px] mb-0 rounded-[9px] bg-casevo-input px-[12px] py-[9px] text-[13px] text-casevo-forest" aria-live="polite">
        {preview ? <>Your next {noun} will be numbered: <b className="font-mono font-semibold">{preview}</b></> : <span className="text-casevo-text-soft">Fix the highlighted setting to see the next number.</span>}
      </p>
    </fieldset>
  )
}

// ── Send bill: the covering email template ───────────────────
// Subject + message sent with every bill (Send bill on an issued bill).
// Merge fields are listed as chips (click → inserted at the cursor of the
// last-focused box) and resolved with example values in the preview; a real
// send resolves them from the bill. Saved with the rest of this panel; the
// server refuses an unknown {Token}. Text equal to the default is stored as
// null so the firm keeps following the default.
const BillEmailTemplateEditor: React.FC<{
  subject: string; body: string; onSubject: (v: string) => void; onBody: (v: string) => void
  fields: readonly string[]; subjectError?: string; bodyError?: string
  isDefault: boolean; onReset: () => void; sample: Record<string, string>
}> = ({ subject, body, onSubject, onBody, fields, subjectError, bodyError, isDefault, onReset, sample }) => {
  const subjectRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const lastFocus = useRef<'subject' | 'body'>('body')
  const insert = (field: string): void => {
    const token = `{${field}}`
    const which = lastFocus.current
    const el = which === 'subject' ? subjectRef.current : bodyRef.current
    const value = which === 'subject' ? subject : body
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const next = value.slice(0, start) + token + value.slice(end)
    if (which === 'subject') onSubject(next); else onBody(next)
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length) })
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-[18px] pt-[14px] pb-[4px]">
      <div className="min-w-0 grid gap-[12px] content-start">
        <FormField label="Subject" variant="info" htmlFor="bill-email-subject">
          <input id="bill-email-subject" ref={subjectRef} className={sInput(!!subjectError)} value={subject} maxLength={300} autoComplete="off"
            onChange={(e) => onSubject(e.target.value)} onFocus={() => { lastFocus.current = 'subject' }} aria-invalid={!!subjectError || undefined} />
          <FieldError message={subjectError} />
        </FormField>
        <FormField label="Message" variant="info" htmlFor="bill-email-body">
          <textarea id="bill-email-body" ref={bodyRef} rows={13} className={`${sInput(!!bodyError)} !h-auto py-[8px] leading-[1.5] resize-y`} value={body} maxLength={5000}
            onChange={(e) => onBody(e.target.value)} onFocus={() => { lastFocus.current = 'body' }} aria-invalid={!!bodyError || undefined} />
          <FieldError message={bodyError} />
        </FormField>
        <div>
          <span className={S_LABEL}>Merge fields</span>
          <p className={`${S_NOTE} mt-0 mb-[8px]`}>Filled in automatically for each bill. Click one to add it where your cursor is.</p>
          <ul className="flex flex-wrap gap-[6px] m-0 p-0 list-none">
            {fields.map((f) => (
              <li key={f}>
                <button type="button" onClick={() => insert(f)} title={MERGE_FIELD_HELP[f]}
                  className="inline-flex items-center h-[28px] px-[9px] rounded-[7px] border border-casevo-border bg-casevo-surface font-mono text-[12px] text-casevo-brand dark:text-[#8fd6b0] hover:border-casevo-brand hover:bg-casevo-mint/40 focus:outline-none casevo-focus">
                  {`{${f}}`}
                </button>
              </li>
            ))}
          </ul>
          {!isDefault && <button type="button" className={`${S_BTN_GHOST_SM} mt-[10px]`} onClick={onReset}>Reset to the standard wording</button>}
        </div>
      </div>
      <div className="min-w-0">
        <span className={S_LABEL}>Preview <span className="normal-case tracking-normal font-normal text-casevo-text-soft">(example values)</span></span>
        <div className="rounded-[12px] border border-casevo-border-soft bg-casevo-input overflow-hidden" aria-live="polite">
          <div className="px-[14px] py-[10px] border-b border-casevo-border-soft text-[13px]">
            <span className="text-casevo-text-soft">Subject: </span><b className="font-semibold text-casevo-forest [overflow-wrap:anywhere]">{resolveMergeFields(subject, sample) || '—'}</b>
          </div>
          <div className="px-[14px] py-[12px] text-[13px] leading-[1.55] text-casevo-forest whitespace-pre-wrap [overflow-wrap:anywhere] max-h-[340px] overflow-y-auto">{resolveMergeFields(body, sample) || '—'}</div>
          <div className="px-[14px] py-[9px] border-t border-casevo-border-soft text-[12px] text-casevo-text-soft">Attachment: {`Invoice ${sample.InvoiceNumber}.pdf`} (added automatically)</div>
        </div>
      </div>
    </div>
  )
}

// ── BRANDING PANEL ───────────────────────────────────────────
// Admin › Branding — the firm LOGO, DOCUMENT COLOUR, optional SECONDARY
// COLOUR and the two DOCUMENT FONTS (Firm.documentBranding + logoKey), moved
// out of Billing. Same endpoint + save rules as before (GET/PATCH the billing
// settings, diff-only patch, server re-validates, admin-only route); form
// drafts until "Save branding", behind the unsaved-changes guard. Used on
// EVERY document Casevo produces — bills, credit notes, statements and case
// exports — whether or not a case export shows its letterhead block.
const BrandingSettingsPanel: React.FC = () => {
  const [loaded, setLoaded] = useState<FirmBillingSettings | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // The firm name on the preview's mock document header.
  const firmName = useCasevoStore((s) => s.authUser?.firmName) ?? ''
  const [logoDraft, setLogoDraft] = useState<string | null>(null)
  const [accent, setAccent] = useState('')
  const [secondary, setSecondary] = useState('')
  const [headingFont, setHeadingFont] = useState('')
  const [bodyFont, setBodyFont] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({})

  const seed = (b: FirmBillingSettings): void => {
    setLoaded(b)
    setLogoDraft(b.logoId)
    setAccent((b.accentColour ?? b.defaultAccentColour).toUpperCase())
    setSecondary((b.secondaryColour ?? '').toUpperCase())
    setHeadingFont(b.headingFont ?? b.defaultDocumentFont)
    setBodyFont(b.bodyFont ?? b.defaultDocumentFont)
  }
  useEffect(() => {
    let cancelled = false
    getBillingSettingsApi()
      .then((b) => { if (!cancelled) seed(b) })
      .catch((err) => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load branding settings') })
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    if (!saved) return
    const t = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(t)
  }, [saved])

  const SUB = 'Used on every document Casevo produces: bills, credit notes, statements and case exports.'
  if (loadError) {
    return (
      <SettingsCard icon="pen" title="Branding" sub={SUB}>
        <p className="text-[13px] text-casevo-danger py-[13px]" role="alert">{loadError}</p>
      </SettingsCard>
    )
  }
  if (!loaded) {
    return (
      <SettingsCard icon="pen" title="Branding" sub={SUB}>
        <p className={`${S_NOTE} py-[13px]`}>Loading branding settings…</p>
      </SettingsCard>
    )
  }

  // The pending logo (or its removal), the colours and the fonts count as
  // unsaved changes like any field. The colour is stored as null when it's the
  // Casevo default (blank also means the default); the secondary as null when
  // cleared; a font as null when it's the default.
  const patch: FirmBillingSettingsPatch = {}
  if (logoDraft !== loaded.logoId) patch.logoId = logoDraft
  const accentNorm = accent.trim() ? normaliseAccent(accent) : loaded.defaultAccentColour
  const accentError = accentNorm ? (fieldErr.accentColour || null) : 'Enter a colour as a hex code, e.g. #2D6A4F.'
  if (accentNorm) {
    const stored = accentNorm === normaliseAccent(loaded.defaultAccentColour) ? null : accentNorm
    if (stored !== (loaded.accentColour ?? null)) patch.accentColour = stored
  }
  const secondaryNorm = secondary.trim() ? normaliseAccent(secondary) : null
  const secondaryError = secondary.trim() && !secondaryNorm ? 'Enter a colour as a hex code, e.g. #B08D57 — or Clear it.' : (fieldErr.secondaryColour || null)
  if (!secondary.trim() || secondaryNorm) {
    if (secondaryNorm !== (loaded.secondaryColour ?? null)) patch.secondaryColour = secondaryNorm
  }
  const storedFont = (id: string): string | null => (!id || id === loaded.defaultDocumentFont ? null : id)
  if (storedFont(headingFont) !== (loaded.headingFont ?? null)) patch.headingFont = storedFont(headingFont)
  if (storedFont(bodyFont) !== (loaded.bodyFont ?? null)) patch.bodyFont = storedFont(bodyFont)
  const dirty = Object.keys(patch).length > 0
  const blocked = !accentNorm || !!(secondary.trim() && !secondaryNorm)

  const save = async (): Promise<void> => {
    if (!dirty || blocked) return
    setSaving(true)
    setError(null)
    setFieldErr({})
    try {
      seed(await patchBillingSettingsApi(patch))
      setSaved(true)
    } catch (err) {
      const data = (err as { response?: { data?: { field?: string; error?: string } } }).response?.data
      // The font pickers have no inline error slot — theirs shows in the banner.
      if (data?.field && data.error && (data.field === 'headingFont' || data.field === 'bodyFont')) setError(data.error)
      else if (data?.field && data.error) setFieldErr({ [data.field]: data.error })
      else setError(err instanceof Error ? err.message : 'Failed to save branding')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard icon="pen" title="Branding" sub={SUB}>
      <p className={`${S_NOTE} pt-[4px]`}>Each issued bill and credit note keeps a frozen copy of the logo, colours and fonts it was issued with, so changing these never alters one already issued.</p>
      <div className={S_FIELDS}>
        <LetterheadBranding firmName={firmName} address={loaded.billingAddress ?? ''}
          logoId={logoDraft} savedLogoId={loaded.logoId} logoError={fieldErr.logoId}
          accent={accent} defaultAccent={loaded.defaultAccentColour} accentError={accentError}
          secondary={secondary} secondaryError={secondaryError}
          headingFont={headingFont} bodyFont={bodyFont} defaultFont={loaded.defaultDocumentFont}
          onLogo={(id) => { setLogoDraft(id); if (fieldErr.logoId) setFieldErr({ ...fieldErr, logoId: '' }) }}
          onAccent={(v) => { setAccent(v.toUpperCase()); if (fieldErr.accentColour) setFieldErr({ ...fieldErr, accentColour: '' }) }}
          onSecondary={(v) => { setSecondary(v.toUpperCase()); if (fieldErr.secondaryColour) setFieldErr({ ...fieldErr, secondaryColour: '' }) }}
          onHeadingFont={setHeadingFont} onBodyFont={setBodyFont} />
      </div>
      <div className={S_FORMFOOT}>
        <button type="button" className={S_BTN_PRIMARY} disabled={!dirty || blocked || saving} onClick={() => { void save() }}>
          {saving ? 'Saving…' : 'Save branding'}
        </button>
        <UnsavedGuard dirty={dirty && !saving} />
        {dirty && !saving && <button type="button" className={S_BTN_GHOST} onClick={() => { seed(loaded); setFieldErr({}); setError(null) }}>Discard changes</button>}
        {saved && <SettingsSaved />}
        {error && <span className="text-[12.5px] text-casevo-danger" role="alert">{error}</span>}
      </div>
    </SettingsCard>
  )
}

const BillingSettingsPanel: React.FC<{ onOpenBranding: () => void; onOpenPiSettlements: () => void; onOpenTeam?: () => void }> = ({ onOpenBranding, onOpenPiSettlements, onOpenTeam }) => {
  const [loaded, setLoaded] = useState<FirmBillingSettings | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [vat, setVat] = useState('')
  const [sra, setSra] = useState('')
  const [invNum, setInvNum] = useState<NumberingState | null>(null)
  const [cnNum, setCnNum] = useState<NumberingState | null>(null)
  // The bill-approval switch saves ON ITS OWN the moment it's flipped (like the
  // 2FA switch) — it's never part of the form's Save, so an invalid field
  // elsewhere on this form can't silently stop it saving.
  const [approval, setApproval] = useState(false)
  const [approvalSaving, setApprovalSaving] = useState(false)
  const [approvalSaved, setApprovalSaved] = useState(false)
  const [approvalError, setApprovalError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({})
  // Invoicing Stage 1 — letterhead + payment details printed on bills.
  const [extra, setExtra] = useState<Record<BillingTextKey, string>>(() => emptyExtra())
  const [terms, setTerms] = useState('30')
  // Send bill — covering email template (shown as the default when unset).
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  // (The logo, document colours and fonts live in Admin › Branding.)
  const authUser = useCasevoStore((s) => s.authUser)

  const seed = (b: FirmBillingSettings): void => {
    setLoaded(b)
    setEmailSubject(b.billEmailSubject ?? b.defaultBillEmailSubject)
    setEmailBody(b.billEmailBody ?? b.defaultBillEmailBody)
    setExtra(Object.fromEntries(BILLING_TEXT_FIELDS.map((f) => [f.key, b[f.key] ?? ''])) as Record<BillingTextKey, string>)
    setTerms(String(b.paymentTermsDays))
    setVat(formatVatInput(b.vatNumber ?? ''))
    setSra(b.sraNumber ?? '')
    setInvNum(numberingFromStored(b.invoiceNumberFormat, b.defaultInvoiceNumberFormat, b.nextInvoiceSeq))
    setCnNum(numberingFromStored(b.creditNoteNumberFormat, b.defaultCreditNoteNumberFormat, b.nextCreditNoteSeq))
    setApproval(b.billApprovalRequired)
  }
  useEffect(() => {
    let cancelled = false
    getBillingSettingsApi()
      .then((b) => { if (!cancelled) seed(b) })
      .catch((err) => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load billing settings') })
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    if (!saved) return
    const t = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(t)
  }, [saved])
  useEffect(() => {
    if (!approvalSaved) return
    const t = setTimeout(() => setApprovalSaved(false), 2000)
    return () => clearTimeout(t)
  }, [approvalSaved])

  // Optimistic flip → PATCH just this field → roll back on failure. Updates
  // only billApprovalRequired in `loaded`, so unsaved edits elsewhere survive.
  const toggleApproval = async (): Promise<void> => {
    const next = !approval
    const previous = approval
    setApprovalError(null)
    setApproval(next)
    setApprovalSaving(true)
    try {
      const b = await patchBillingSettingsApi({ billApprovalRequired: next })
      setApproval(b.billApprovalRequired)
      setLoaded((prev) => (prev ? { ...prev, billApprovalRequired: b.billApprovalRequired } : prev))
      setApprovalSaved(true)
    } catch (err) {
      setApproval(previous)
      const data = (err as { response?: { data?: { error?: string } } }).response?.data
      setApprovalError(data?.error ?? (err instanceof Error ? err.message : 'Failed to save the approval setting'))
    } finally {
      setApprovalSaving(false)
    }
  }

  if (loadError) {
    return (
      <SettingsCard icon="file" title="Billing" sub="Firm details printed on bills and the rules bills follow.">
        <p className="text-[13px] text-casevo-danger py-[13px]" role="alert">{loadError}</p>
      </SettingsCard>
    )
  }
  if (!loaded || !invNum || !cnNum) {
    return (
      <SettingsCard icon="file" title="Billing" sub="Firm details printed on bills and the rules bills follow.">
        <p className={`${S_NOTE} py-[13px]`}>Loading billing settings…</p>
      </SettingsCard>
    )
  }

  // Client-side checks mirror the server's (it re-validates on save).
  // Numbering: the builders assemble the stored format string; the preview
  // uses the same formatInvoiceNumber the server issues with (UTC year/month).
  const effectiveFmt = numberingFormat(invNum)
  const fmtError = numberingFormatError(invNum)
  const invStartError = numberingStartError(invNum, loaded.nextInvoiceSeq, loaded.invoicesIssued, 'bill', 'bills')
  const preview = fmtError || invStartError ? null : formatInvoiceNumber(effectiveFmt, Number(invNum.start))
  // Invoicing Stage 2 — credit notes: own counter + template.
  const effectiveCn = numberingFormat(cnNum)
  const cnError = numberingFormatError(cnNum)
  const cnStartError = numberingStartError(cnNum, loaded.nextCreditNoteSeq, loaded.creditNotesIssued, 'credit note', 'credit notes')
  const cnClash = !cnError && !fmtError && effectiveCn.toUpperCase() === effectiveFmt.toUpperCase()
    ? `Credit notes would be numbered exactly like bills (both ${formatInvoiceNumber(effectiveFmt, 1)}…). Give credit notes a different prefix, such as CN, so they can’t be confused with bills.` : null
  const cnPreview = cnError || cnClash || cnStartError ? null : formatInvoiceNumber(effectiveCn, Number(cnNum.start))
  // VAT: optional "GB" + exactly 9 digits; spaces ignored; stored as "GB123456789"
  // (mirrors normaliseVat in firmSettingsController). SRA: the SRA's own register
  // shows 6-digit IDs and newer 7-digit ones (e.g. 8000448), so both are accepted.
  // Only validated once edited, so a value saved under the older, looser rules
  // never blocks saving the rest of this form.
  const vatNorm = vatCanonical(vat)
  const vatCompact = vat.replace(/\s+/g, '').toUpperCase()
  const vatError = vatCompact && vatCompact !== (loaded.vatNumber ?? '') && !vatNorm ? 'Enter GB followed by 9 digits (or 12 for a branch), e.g. GB 123 4567 89.' : null
  const sraTrim = sra.replace(/\s+/g, '')
  const sraError = sraTrim && sraTrim !== (loaded.sraNumber ?? '') && !/^\d{6,7}$/.test(sraTrim) ? 'Enter the 6- or 7-digit SRA number, e.g. 123456.' : null
  const bankErr: Partial<Record<BillingTextKey, string>> = {}
  if (extra.bankSortCode.trim() && !/^\d{2}-\d{2}-\d{2}$/.test(extra.bankSortCode.trim())) bankErr.bankSortCode = 'Enter a 6-digit sort code, e.g. 20-45-77.'
  if (extra.bankAccountNumber.trim() && !/^\d{8}$/.test(extra.bankAccountNumber.trim())) bankErr.bankAccountNumber = 'Enter the 8-digit account number.'

  const patch: FirmBillingSettingsPatch = {}
  // Stored unspaced ("GB123456789"); the spaces are display-only.
  const vatOut = vatCompact ? (vatNorm ?? vatCompact) : null
  if (vatOut !== (loaded.vatNumber ?? null)) patch.vatNumber = vatOut
  if ((sraTrim || null) !== (loaded.sraNumber ?? null)) patch.sraNumber = sraTrim || null
  // Only send a format when it differs from what's in force (stored, or the default when unset).
  if (effectiveFmt !== (loaded.invoiceNumberFormat ?? loaded.defaultInvoiceNumberFormat)) patch.invoiceNumberFormat = effectiveFmt
  if (effectiveCn !== (loaded.creditNoteNumberFormat ?? loaded.defaultCreditNoteNumberFormat)) patch.creditNoteNumberFormat = effectiveCn
  if (!invStartError && Number(invNum.start) !== loaded.nextInvoiceSeq) patch.nextInvoiceSeq = Number(invNum.start)
  if (!cnStartError && Number(cnNum.start) !== loaded.nextCreditNoteSeq) patch.nextCreditNoteSeq = Number(cnNum.start)
  for (const f of BILLING_TEXT_FIELDS) {
    const v = extra[f.key].trim() || null
    if (v !== (loaded[f.key] ?? null)) (patch as Record<string, unknown>)[f.key] = v
  }
  const termsNum = Number(terms)
  const termsError = !/^\d{1,3}$/.test(terms.trim()) || termsNum > 365 ? 'Enter 0–365 days.' : null
  if (!termsError && termsNum !== loaded.paymentTermsDays) patch.paymentTermsDays = termsNum
  // Send bill template — blank or the default wording → stored as null.
  const tmplErr = (v: string, what: string, oneLine: boolean): string | undefined => {
    if (!v.trim()) return `Enter the ${what}.`
    if (oneLine && /[\r\n]/.test(v)) return 'Keep the subject to one line.'
    const unknown = unknownMergeFields(v, loaded.billMergeFields)
    return unknown.length ? `${unknown.map((u) => `{${u}}`).join(', ')} ${unknown.length === 1 ? 'isn’t a merge field' : 'aren’t merge fields'} — use the ones listed below.` : undefined
  }
  const subjectErr = tmplErr(emailSubject, 'email subject', true) ?? fieldErr.billEmailSubject
  const bodyErr = tmplErr(emailBody, 'email message', false) ?? fieldErr.billEmailBody
  const storedTmpl = (v: string, def: string): string | null => (v.trim() === def.trim() ? null : v.trim())
  if (storedTmpl(emailSubject, loaded.defaultBillEmailSubject) !== (loaded.billEmailSubject ?? null)) patch.billEmailSubject = storedTmpl(emailSubject, loaded.defaultBillEmailSubject)
  if (storedTmpl(emailBody, loaded.defaultBillEmailBody) !== (loaded.billEmailBody ?? null)) patch.billEmailBody = storedTmpl(emailBody, loaded.defaultBillEmailBody)
  const dirty = Object.keys(patch).length > 0
  const blocked = !!(fmtError || cnError || cnClash || invStartError || cnStartError || vatError || sraError || termsError || bankErr.bankSortCode || bankErr.bankAccountNumber || subjectErr || bodyErr)

  const save = async (): Promise<void> => {
    if (!dirty || blocked) return
    setSaving(true)
    setError(null)
    setFieldErr({})
    try {
      seed(await patchBillingSettingsApi(patch))
      setSaved(true)
    } catch (err) {
      const data = (err as { response?: { data?: { field?: string; error?: string } } }).response?.data
      if (data?.field && data.error) setFieldErr({ [data.field]: data.error })
      else setError(err instanceof Error ? err.message : 'Failed to save billing settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard icon="file" title="Billing" sub="Firm details printed on bills, how bills are numbered, and the rules they follow before they’re issued.">
      {/* Bill approval — the ONE switch. Saves on its own (not with the form below). */}
      <SettingsToggleRow
        title="Require bills to be approved before they can be issued"
        desc={approval
          ? 'On — only admins and members with “Approve bills” (Admin › Permissions) can approve bills. Everyone else sends a bill for approval (approvers are notified), then anyone on the case can issue it once it’s approved.'
          : 'Off — anyone who can work on a case can issue its bills directly.'}
        control={<SettingsSwitch checked={approval} onChange={() => { void toggleApproval() }} label="Require bills to be approved before they can be issued" disabled={approvalSaving} />}
      />
      {(approvalSaving || approvalSaved || approvalError) && (
        <div className="flex flex-wrap items-center gap-[10px] pb-[10px]">
          {approvalSaving && <span className={S_NOTE}>Saving…</span>}
          {approvalSaved && <SettingsSaved />}
          {approvalError && <span className="text-[12.5px] text-casevo-danger" role="alert">{approvalError}</span>}
        </div>
      )}
      <div className={S_FIELDS}>
        <div>
          <label className={S_LABEL} htmlFor="bill-vat">VAT number</label>
          <input id="bill-vat" className={sInput(!!(vatError || fieldErr.vatNumber))} value={vat} onChange={(e) => setVat(formatVatInput(e.target.value))}
            placeholder="GB 123 4567 89" autoComplete="off" maxLength={24}
            aria-invalid={!!(vatError || fieldErr.vatNumber) || undefined} aria-describedby="bill-vat-help" />
          {(vatError || fieldErr.vatNumber)
            ? <p id="bill-vat-help" className={S_FIELD_ERROR}>{vatError || fieldErr.vatNumber}</p>
            : <p id="bill-vat-help" className={`${S_NOTE} mt-[5px]`}>GB + 9 digits (12 for a branch); spaces are added for you. Leave blank if the firm isn’t VAT-registered.</p>}
        </div>
        <div>
          <label className={S_LABEL} htmlFor="bill-sra">SRA number</label>
          <input id="bill-sra" className={sInput(!!(sraError || fieldErr.sraNumber))} value={sra} onChange={(e) => setSra(e.target.value.replace(/[^\d\s]/g, ''))} inputMode="numeric" placeholder="123456" autoComplete="off" maxLength={9}
            aria-invalid={!!(sraError || fieldErr.sraNumber) || undefined} aria-describedby="bill-sra-help" />
          {(sraError || fieldErr.sraNumber)
            ? <p id="bill-sra-help" className={S_FIELD_ERROR}>{sraError || fieldErr.sraNumber}</p>
            : <p id="bill-sra-help" className={`${S_NOTE} mt-[5px]`}>The firm’s SRA ID — 6 or 7 digits, as on the SRA register.</p>}
        </div>
        {/* The old "Default CFA success fee %" (Firm.defaultSuccessPercent,
            deprecated — column kept) moved to Admin › PI settlements. */}
        <div className="sm:col-span-2">
          <p className={`${S_NOTE} m-0`}>
            <button type="button" onClick={onOpenPiSettlements} className="font-semibold text-casevo-brand hover:underline focus:outline-none casevo-focus rounded">Success fee defaults are now in Admin › PI settlements →</button>
          </p>
        </div>
      </div>
      <div className="pt-[18px] mt-[8px] border-t border-casevo-border-soft">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">Bill &amp; credit note numbers</h3>
        <p className={`${S_NOTE} mt-[2px]`}>Numbers are given when a bill or credit note is issued — drafts stay unnumbered. Credit notes have their own running number, separate from bills.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[14px] pt-[14px] pb-[4px]">
        <NumberingBuilder id="num-inv" title="Bill numbers" noun="bill" nouns="bills" examplePrefix="MMA" state={invNum} onChange={setInvNum}
          issued={loaded.invoicesIssued} next={loaded.nextInvoiceSeq} formatError={fmtError} startError={invStartError} clash={null}
          serverFormatError={fieldErr.invoiceNumberFormat} serverStartError={fieldErr.nextInvoiceSeq} preview={preview}
          resetParts={parseNumberFormat(loaded.defaultInvoiceNumberFormat) ?? { prefix: 'INV', year: 'YYYY', month: false, pad: 4 }} />
        <NumberingBuilder id="num-cn" title="Credit note numbers" noun="credit note" nouns="credit notes" examplePrefix="CN" state={cnNum} onChange={setCnNum}
          issued={loaded.creditNotesIssued} next={loaded.nextCreditNoteSeq} formatError={cnError} startError={cnStartError} clash={cnClash}
          serverFormatError={fieldErr.creditNoteNumberFormat} serverStartError={fieldErr.nextCreditNoteSeq} preview={cnPreview}
          resetParts={parseNumberFormat(loaded.defaultCreditNoteNumberFormat) ?? { prefix: 'CN', year: 'YYYY', month: false, pad: 4 }} />
      </div>
      <div className="pt-[18px] mt-[8px] border-t border-casevo-border-soft">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">Letterhead &amp; payment details</h3>
        <p className={`${S_NOTE} mt-[2px]`}>Printed on every bill, credit note, statement and case summary. Each issued bill and credit note keeps a frozen copy, so changing these never alters one already issued.</p>
        <p className={`${S_NOTE} mt-[6px]`}>
          Logo, document colours and fonts:{' '}
          <button type="button" onClick={onOpenBranding} className="font-semibold text-casevo-brand hover:underline focus:outline-none casevo-focus rounded">Branding settings →</button>
        </p>
      </div>
      <div className={S_FIELDS}>
        {BILLING_TEXT_FIELDS.map((f) => (
          <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
            <label className={S_LABEL} htmlFor={`bill-${f.key}`}>{f.label}</label>
            {f.wide
              ? <textarea id={`bill-${f.key}`} rows={f.key === 'billingAddress' ? 3 : 2} className={`${sInput(!!fieldErr[f.key])} !h-auto py-[8px]`} value={extra[f.key]} onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })} placeholder={f.placeholder} />
              : <input id={`bill-${f.key}`} className={sInput(!!(bankErr[f.key] || fieldErr[f.key]))} value={extra[f.key]} onChange={(e) => setExtra({ ...extra, [f.key]: normaliseBillingText(f.key, e.target.value) })} placeholder={f.placeholder} autoComplete="off" inputMode={f.numeric ? 'numeric' : undefined} maxLength={f.key === 'bankSortCode' || f.key === 'bankAccountNumber' ? 8 : undefined} aria-invalid={!!(bankErr[f.key] || fieldErr[f.key]) || undefined} />}
            {(bankErr[f.key] || fieldErr[f.key]) && <p className={S_FIELD_ERROR}>{bankErr[f.key] || fieldErr[f.key]}</p>}
          </div>
        ))}
        <div>
          <label className={S_LABEL} htmlFor="bill-terms">Payment terms (days)</label>
          <input id="bill-terms" className={`${sInput(!!(termsError || fieldErr.paymentTermsDays))} w-[110px]`} value={terms} onChange={(e) => setTerms(e.target.value)} inputMode="numeric" />
          {(termsError || fieldErr.paymentTermsDays) ? <p className={S_FIELD_ERROR}>{termsError || fieldErr.paymentTermsDays}</p> : <p className={`${S_NOTE} mt-[5px]`}>New bills are due this many days after they’re issued.</p>}
        </div>
      </div>
      <div className="pt-[18px] mt-[8px] border-t border-casevo-border-soft">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">Signature</h3>
        {/* B3c — one line, both places as links. */}
        <SignaturePerPersonNote onOpenTeam={onOpenTeam} />
      </div>
      <div className="pt-[10px]"><FirmSignatureMoveCard /></div>
      <div className="pt-[18px] mt-[8px] border-t border-casevo-border-soft">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">Bill email</h3>
        <p className={`${S_NOTE} mt-[2px]`}>The covering message sent with the bill PDF when someone uses “Send bill”. They see it filled in and can change it before sending.</p>
      </div>
      <BillEmailTemplateEditor
        subject={emailSubject} body={emailBody}
        onSubject={(v) => { setEmailSubject(v); if (fieldErr.billEmailSubject) setFieldErr({ ...fieldErr, billEmailSubject: '' }) }}
        onBody={(v) => { setEmailBody(v); if (fieldErr.billEmailBody) setFieldErr({ ...fieldErr, billEmailBody: '' }) }}
        fields={loaded.billMergeFields} subjectError={subjectErr} bodyError={bodyErr}
        isDefault={emailSubject.trim() === loaded.defaultBillEmailSubject.trim() && emailBody.trim() === loaded.defaultBillEmailBody.trim()}
        onReset={() => { setEmailSubject(loaded.defaultBillEmailSubject); setEmailBody(loaded.defaultBillEmailBody) }}
        sample={sampleMergeValues(authUser?.firmName ?? '', `${authUser?.firstName ?? ''} ${authUser?.lastName ?? ''}`.trim(), Number(terms) || loaded.paymentTermsDays)} />
      <div className={S_FORMFOOT}>
        <button type="button" className={S_BTN_PRIMARY} disabled={!dirty || blocked || saving} onClick={() => { void save() }}>
          {saving ? 'Saving…' : 'Save billing settings'}
        </button>
        <UnsavedGuard dirty={dirty && !saving} />
        {dirty && !saving && <button type="button" className={S_BTN_GHOST} onClick={() => { seed(loaded); setFieldErr({}); setError(null) }}>Discard changes</button>}
        {saved && <SettingsSaved />}
        {error && <span className="text-[12.5px] text-casevo-danger" role="alert">{error}</span>}
      </div>
    </SettingsCard>
  )
}

// ── INVITES PANEL ────────────────────────────────────────────
// Admin-only team invites surface. Lives inline alongside the other
// Admin panels in this file. Gated by the parent's authUser?.role ===
// 'admin' check (AdminPage's render-time guard).
//
// Behaviour summary:
//   - Loads ALL the firm's invites on mount via listInvitesApi (no
//     status filter — admin wants the full history including revoked
//     and accepted, not just pending).
//   - Add form at the top: email text input + role <select> (default
//     'user') + "Send invite" button. POSTs via createInviteApi; on
//     success the returned Invite is prepended to the list. If
//     emailSent === false the panel shows a sticky muted note rather
//     than the usual 2-second "Invite sent ✓" tick — the admin needs
//     to know to retry via Resend.
//   - List rows show email + status chip + role + expiry date. Per-row
//     actions:
//       Resend  — POST /api/invites/:id/resend. Visible for
//                 PENDING / REVOKED / EXPIRED rows (revives a dead
//                 invite back to PENDING). Hidden for ACCEPTED.
//       Revoke  — POST /api/invites/:id/revoke. Visible for PENDING
//                 only; gated by a ConfirmDialogue (same destructive-
//                 action pattern TeamPanel + DeadlineTypesPanel use).
//                 Hidden for ACCEPTED / REVOKED / EXPIRED.
//   - Per-row state (resendingId / revokingId / rowErrors) keyed by
//     invite id so one row's in-flight or error never affects another.
//   - savedId drives the 2-second "Resent ✓" tick on Resend success,
//     mirroring TeamPanel + DeadlineTypesPanel.
//
// NO TOKEN HANDLING ANYWHERE: the invite token is delivered to the
// recipient via email only; never returned by these admin endpoints
// (see invite.types.ts). The panel reads Invite.id for all per-row
// operations — never the token.
const InvitesPanel: React.FC = () => {
  // null = loading, [] = empty, [...] = loaded
  const [invites, setInvites] = useState<Invite[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Add-form state. addError surfaces backend Error.message (e.g. 409
  // duplicate-email) inline near the add input.
  const [addEmail, setAddEmail] = useState<string>('')
  const [addRole, setAddRole] = useState<InviteRole>('user')
  const [adding, setAdding] = useState<boolean>(false)
  const [addError, setAddError] = useState<string | null>(null)
  // lastCreated drives the post-add feedback:
  //   - emailSent !== false → 2-second "Invite sent ✓" tick (auto-fade
  //     via the effect below).
  //   - emailSent === false → sticky muted "email failed" note until
  //     the next add or a manual dismiss.
  const [lastCreated, setLastCreated] = useState<Invite | null>(null)
  // Per-row in-flight + savedId pattern. Mirrors TeamPanel +
  // DeadlineTypesPanel. resendingId and revokingId are separate
  // because a single row can have at most one operation in flight
  // at a time (revoke is gated by confirm first, so it can't race
  // with resend), but separate ids keep each button's disable state
  // clean.
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  // Revoke confirmation. Carries the candidate row so the dialogue
  // can name them and the confirm handler knows which id to POST.
  const [confirmRevoke, setConfirmRevoke] = useState<Invite | null>(null)

  // Mount-time fetch. Cancelled-flag pattern same as TeamPanel +
  // DeadlineTypesPanel. No status filter on listInvitesApi — the admin
  // wants the full history (PENDING + ACCEPTED + REVOKED + EXPIRED)
  // so they can scroll back through revocations and resends.
  // (D12 live updates) Another admin invited / revoked, or an invite was
  // accepted (firm-changed ['users']) → re-read.
  const [invitesRev, setInvitesRev] = useState(0)
  useFirmChange(['users'], () => setInvitesRev((n) => n + 1))
  useEffect(() => {
    let cancelled = false
    listInvitesApi().then((data) => {
      if (!cancelled) setInvites(data)
    }).catch((err) => {
      if (cancelled) return
      const message = err instanceof Error ? err.message : 'Failed to load invites'
      setLoadError(message)
    })
    return () => { cancelled = true }
  }, [invitesRev])

  // Auto-fade the post-add "Invite sent ✓" tick — same 2 s pattern as
  // TeamPanel + DeadlineTypesPanel. Skipped when emailSent === false:
  // that case shows a sticky muted note instead, which the admin
  // dismisses manually (or it's overwritten by the next add).
  useEffect(() => {
    if (!lastCreated) return
    if (lastCreated.emailSent === false) return
    const timer = setTimeout(() => setLastCreated(null), 2000)
    return () => clearTimeout(timer)
  }, [lastCreated])

  // Auto-fade the per-row "Resent ✓" tick — same 2 s pattern.
  useEffect(() => {
    if (!savedId) return
    const timer = setTimeout(() => setSavedId(null), 2000)
    return () => clearTimeout(timer)
  }, [savedId])

  // ── ADD HANDLER ──────────────────────────────────────────
  const handleAdd = async (): Promise<void> => {
    const email = addEmail.trim()
    if (!email) return
    setAddError(null)
    setAdding(true)
    try {
      const created = await createInviteApi({ email, role: addRole })
      // Prepend (newest first) — matches the natural admin scan order
      // and avoids re-fetching the whole list for a single addition.
      setInvites((list) => list ? [created, ...list] : [created])
      setAddEmail('')
      setLastCreated(created)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send invite'
      setAddError(message)
    } finally {
      setAdding(false)
    }
  }

  // ── RESEND HANDLER ───────────────────────────────────────
  const handleResend = async (invite: Invite): Promise<void> => {
    setResendingId(invite.id)
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[invite.id]
      return next
    })
    try {
      const updated = await resendInviteApi(invite.id)
      // Replace the row in-place. Resend revives REVOKED/EXPIRED back
      // to PENDING (status flip lives on the returned Invite) and
      // refreshes expiresAt; both surface via the in-place swap.
      setInvites((list) => list ? list.map((i) => i.id === invite.id ? updated : i) : list)
      setSavedId(invite.id)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend invite'
      setRowErrors((prev) => ({ ...prev, [invite.id]: message }))
    } finally {
      setResendingId(null)
    }
  }

  // ── REVOKE HANDLER ───────────────────────────────────────
  // Fires only after the ConfirmDialogue's confirm button is clicked.
  // On success: replace the row in-place with the updated invite
  // (status flips to REVOKED). On failure: write the backend message
  // into rowErrors so it surfaces under the row, AND close the modal
  // so the inline error isn't hidden behind it. Mirrors
  // DeadlineTypesPanel.handleConfirmDelete.
  const handleConfirmRevoke = async (): Promise<void> => {
    const invite = confirmRevoke
    if (!invite) return
    setRevokingId(invite.id)
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[invite.id]
      return next
    })
    try {
      const updated = await revokeInviteApi(invite.id)
      setInvites((list) => list ? list.map((i) => i.id === invite.id ? updated : i) : list)
      setConfirmRevoke(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revoke invite'
      setRowErrors((prev) => ({ ...prev, [invite.id]: message }))
      // Close the modal so the inline row error becomes visible.
      setConfirmRevoke(null)
    } finally {
      setRevokingId(null)
    }
  }

  // ── HELPERS ──────────────────────────────────────────────
  // Status chip styling. Mirrors the status-chip recipe used elsewhere
  // (CaseListScreen / ClientSearchView) — light bg + matching-tone text,
  // paired with dark variants where the existing chips have them.
  const statusChip = (status: InviteStatus): { label: string; colour: string } => {
    switch (status) {
      case 'PENDING':
        return { label: 'Pending', colour: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' }
      case 'ACCEPTED':
        return { label: 'Accepted', colour: 'bg-casevo-mint text-casevo-brand' }
      case 'REVOKED':
        return { label: 'Revoked', colour: 'bg-gray-100 dark:bg-casevo-border text-casevo-text-soft' }
      case 'EXPIRED':
        return { label: 'Expired', colour: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300' }
    }
  }
  // en-GB short-month formatter for the expiry date — mirrors
  // formatReadableDate in utils/auditSentence.tsx. Inlined (rather than
  // imported) to keep this panel self-contained.
  const formatExpiry = (iso: string | null): string => {
    if (!iso) return '—'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(d)
  }

  const statusBadge = (status: InviteStatus): React.ReactNode => {
    const label = statusChip(status).label
    if (status === 'PENDING') {
      return (
        <span className="inline-flex items-center text-[11.5px] font-semibold px-[10px] py-[3px] rounded-full whitespace-nowrap border bg-[#fff7e6] text-[#7a5a12] border-[#f1d9a6] dark:bg-[#3a3220] dark:text-[#f1d9a6] dark:border-[#5c4c26]">
          {label}
        </span>
      )
    }
    if (status === 'ACCEPTED') return <SettingsBadge on dot>{label}</SettingsBadge>
    return <SettingsBadge>{label}</SettingsBadge>
  }
  // Unsaved: an email typed into the invite form but not sent.
  useUnsavedChangesGuard(addEmail.trim() !== '' && !adding)
  return (
    <>
      <SettingsCard icon="mail" title="Invite someone" sub="They’ll get an email link to set a password and join your firm.">
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_160px_auto] gap-[12px] items-end pt-[6px]">
          <div>
            <label htmlFor="invite-email" className={S_LABEL}>Email</label>
            <input
              id="invite-email"
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd() } }}
              placeholder="name@example.com"
              disabled={adding}
              autoComplete="off"
              className={sInput()}
            />
          </div>
          <div>
            <label htmlFor="invite-role" className={S_LABEL}>Role</label>
            <select
              id="invite-role"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as InviteRole)}
              disabled={adding}
              className={`${A_SELECT_40} w-full`}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button type="button" onClick={() => { void handleAdd() }} disabled={adding || !addEmail.trim()} className={S_BTN_PRIMARY}>
            <SIcon name="send" size={14} />
            {adding ? 'Sending…' : 'Send invite'}
          </button>
        </div>
        {addError && <p className={S_FIELD_ERROR}>{addError}</p>}
        {lastCreated && lastCreated.emailSent !== false && (
          <div className="mt-[10px]"><SettingsSaved>Invite sent</SettingsSaved></div>
        )}
        {lastCreated && lastCreated.emailSent === false && (
          <div className="flex items-start justify-between gap-[12px] mt-[12px] px-[14px] py-[11px] rounded-[10px] bg-casevo-mint border border-casevo-border text-[13px] text-casevo-forest">
            <p className="flex-1 leading-relaxed">Invite created, but the email failed to send — use Resend.</p>
            <button type="button" onClick={() => setLastCreated(null)} aria-label="Dismiss notice" className="shrink-0 text-casevo-text-soft hover:text-casevo-forest rounded-sm focus:outline-none casevo-focus">
              <SIcon name="x" size={16} />
            </button>
          </div>
        )}
      </SettingsCard>
      <SettingsCard icon="list" title="Invites" sub="Pending, accepted, expired and revoked invites.">
        {loadError ? (
          <p className="text-[13px] text-casevo-danger py-[13px]">{loadError}</p>
        ) : invites === null ? (
          <p className={`${S_NOTE} py-[13px]`}>Loading invites…</p>
        ) : invites.length === 0 ? (
          <p className={`${S_NOTE} py-[13px]`}>No invites yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13.5px] [&_tr:last-child_td]:border-b-0">
              <thead>
                <tr>
                  <th className={A_TH}>Email</th>
                  <th className={A_TH}>Role</th>
                  <th className={A_TH}>Status</th>
                  <th className={`${A_TH} hidden sm:table-cell`}>Expires</th>
                  <th className={A_TH}><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => {
                  const isResending = resendingId === invite.id
                  const isRevoking = revokingId === invite.id
                  const isSaved = savedId === invite.id
                  const rowError = rowErrors[invite.id]
                  const showResend = invite.status === 'PENDING' || invite.status === 'REVOKED' || invite.status === 'EXPIRED'
                  const showRevoke = invite.status === 'PENDING'
                  return (
                    <tr key={invite.id} className="hover:bg-[#f6faf8] dark:hover:bg-[#2b3b31]">
                      <td className={`${A_TD} text-casevo-forest break-all`}>
                        {invite.email}
                        {rowError && <p className={S_FIELD_ERROR}>{rowError}</p>}
                      </td>
                      <td className={`${A_TD} text-casevo-forest capitalize`}>{invite.role}</td>
                      <td className={A_TD}>{statusBadge(invite.status)}</td>
                      <td className={`${A_TD} hidden sm:table-cell ${S_NOTE}`}>{formatExpiry(invite.expiresAt)}</td>
                      <td className={`${A_TD} text-right whitespace-nowrap`}>
                        <span className="inline-flex items-center gap-[8px]">
                          {isSaved && <SettingsSaved>Resent</SettingsSaved>}
                          {showResend && (
                            <button type="button" onClick={() => { void handleResend(invite) }} disabled={isResending || isRevoking} className={S_BTN_GHOST_SM}>
                              {isResending ? 'Resending…' : 'Resend'}
                            </button>
                          )}
                          {showRevoke && (
                            <button type="button" onClick={() => setConfirmRevoke(invite)} disabled={isResending || isRevoking} className={S_BTN_DANGER_SM}>
                              {isRevoking ? 'Revoking…' : 'Revoke'}
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>
      <ConfirmDialogue
        isOpen={confirmRevoke !== null}
        title="Revoke this invite?"
        message={
          confirmRevoke
            ? `The invite to ${confirmRevoke.email} will be cancelled. They can no longer use the existing link to join.`
            : ''
        }
        confirmLabel={revokingId ? 'Revoking...' : 'Revoke'}
        onConfirm={() => { void handleConfirmRevoke() }}
        onCancel={() => setConfirmRevoke(null)}
      />
    </>
  )
}

// ── CASE HISTORY VISIBILITY PANEL (admin) ────────────────────
// Firm-level setting: who (besides admins) may view a case's History
// section. Radio single-select (admin | assignee | firm). Save writes
// PUT /api/firm/case-history-visibility AND updates the cached store
// value so the app suppresses/shows the History section without a reload.
// The firm-WIDE audit log stays admin-only regardless of this setting —
// this governs per-case history only. Gated by AdminPage's page-level
// admin guard — no per-panel gate needed.
const CASE_HISTORY_OPTIONS: { value: CaseHistoryVisibility; label: string; help: string }[] = [
  { value: 'admin',    label: 'Admins only',                     help: 'Only firm admins can open a case’s History.' },
  { value: 'assignee', label: 'Admins and the case’s team', help: 'The case’s owner and everyone on its handling team can see its History. Admins always have access.' },
  { value: 'firm',     label: 'Everyone in the firm',            help: 'Any member of the firm can view any case’s History.' },
]
const CaseHistoryVisibilityPanel: React.FC = () => {
  const setCaseHistoryVisibility = useCasevoStore((s) => s.setCaseHistoryVisibility)
  // Selected value. Seeded to the schema default "assignee" so a sensible
  // state shows before the fetch resolves; the loaded value replaces it.
  const [selected, setSelected] = useState<CaseHistoryVisibility>('assignee')
  // The saved value — selected ≠ saved = unsaved (UnsavedGuard below).
  const [savedValue, setSavedValue] = useState<CaseHistoryVisibility>('assignee')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load the current value on mount. Cancelled-flag pattern mirrors the
  // other admin panels.
  useEffect(() => {
    let cancelled = false
    getCaseHistoryVisibilityApi()
      .then((value) => { if (!cancelled) { setSelected(value); setSavedValue(value) } })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load setting') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // "Saved ✓" auto-fade — same 2s pattern as the other admin panels.
  useEffect(() => {
    if (!saved) return
    const timer = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(timer)
  }, [saved])

  const choose = (value: CaseHistoryVisibility): void => {
    setSelected(value)
    setSaved(false)
  }
  const handleSave = async (): Promise<void> => {
    setError(null)
    setSaving(true)
    try {
      const nowSaved = await putCaseHistoryVisibilityApi(selected)
      // Update the cached store value so History suppression across the
      // app reflects the change without a reload.
      setCaseHistoryVisibility(nowSaved)
      setSavedValue(nowSaved)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard
      icon="history"
      title="Case history visibility"
      sub="Who, besides admins, can open a case’s History section. Admins always have access. This controls per-case history only — the firm-wide audit log is always admin-only."
    >
      {loading ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading setting…</p>
      ) : (
        <>
          <div className="pt-[8px]">
            <RadioOptionList options={CASE_HISTORY_OPTIONS} value={selected} onChange={choose} label="Case history visibility" disabled={saving} />
          </div>
          <UnsavedGuard dirty={!saving && selected !== savedValue} />
          {error && <p className="text-[12.5px] text-casevo-danger mt-[12px]" role="alert">{error}</p>}
          <div className={S_FORMFOOT}>
            {saved && <SettingsSaved />}
            <span className="flex-1" />
            <button type="button" onClick={() => { void handleSave() }} disabled={saving} className={S_BTN_PRIMARY}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </SettingsCard>
  )
}

// ── TASK COMPLETION NOTIFICATION ROUTING PANEL (admin) ───────
// Chooses who gets notified when a task is completed by someone other
// than its creator. Writes PUT /api/firm/task-complete-notify. Unlike
// case-history-visibility there's no cached store value to update — the
// routing is consulted server-side at notify time, so a plain save is
// enough. Gated by AdminPage's page-level admin guard.
const TASK_NOTIFY_OPTIONS: { value: TaskCompleteNotify; label: string; help: string }[] = [
  { value: 'creator',            label: 'The task’s creator only',      help: 'Whoever created the task is notified when someone else completes it.' },
  { value: 'creator_and_admins', label: 'The creator and all admins',   help: 'The creator plus every firm admin are notified on completion.' },
  { value: 'admins',             label: 'All admins only',              help: 'Every firm admin is notified; the creator is not (unless they’re an admin).' },
]
const TaskCompleteNotifyPanel: React.FC = () => {
  // Seeded to the schema default "creator" so a sensible state shows
  // before the fetch resolves; the loaded value replaces it.
  const [selected, setSelected] = useState<TaskCompleteNotify>('creator')
  // The saved value — selected ≠ saved = unsaved (UnsavedGuard below).
  const [savedValue, setSavedValue] = useState<TaskCompleteNotify>('creator')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getTaskCompleteNotifyApi()
      .then((value) => { if (!cancelled) { setSelected(value); setSavedValue(value) } })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load setting') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!saved) return
    const timer = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(timer)
  }, [saved])

  const choose = (value: TaskCompleteNotify): void => {
    setSelected(value)
    setSaved(false)
  }
  const handleSave = async (): Promise<void> => {
    setError(null)
    setSaving(true)
    try {
      const nowSaved = await putTaskCompleteNotifyApi(selected)
      setSelected(nowSaved)
      setSavedValue(nowSaved)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard
      icon="bell"
      title="Task completion notifications"
      sub="Who gets notified when a task is completed by someone other than the person who created it. Recipients see it in their activity feed and, unless they’ve turned it off, get an email. Completing your own task never notifies you."
    >
      {loading ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading setting…</p>
      ) : (
        <>
          <div className="pt-[8px]">
            <RadioOptionList options={TASK_NOTIFY_OPTIONS} value={selected} onChange={choose} label="Task completion notifications" disabled={saving} />
          </div>
          <UnsavedGuard dirty={!saving && selected !== savedValue} />
          {error && <p className="text-[12.5px] text-casevo-danger mt-[12px]" role="alert">{error}</p>}
          <div className={S_FORMFOOT}>
            {saved && <SettingsSaved />}
            <span className="flex-1" />
            <button type="button" onClick={() => { void handleSave() }} disabled={saving} className={S_BTN_PRIMARY}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </SettingsCard>
  )
}

// ── REQUIRE 2FA PANEL (admin) ────────────────────────────────
// Firm-level 2FA enforcement toggle. Reads getRequire2faApi() on mount;
// flipping the switch writes setRequire2faApi(value) optimistically and
// rolls back on failure. When ON, every member is forced through 2FA
// setup (App.tsx's ForcedTwoFactorSetup gate) before they can use the
// app. Cloned from CaseHistoryVisibilityPanel's shape; admin-only via
// AdminPage's page-level guard (the PUT is also requireAdmin server-side).
const Require2faPanel: React.FC = () => {
  const [value, setValue] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load current value on mount (cancelled-flag pattern, as siblings do).
  useEffect(() => {
    let cancelled = false
    getRequire2faApi()
      .then((v) => { if (!cancelled) setValue(v) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load setting') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // "Saved ✓" auto-fade — same 2s pattern as the other admin panels.
  useEffect(() => {
    if (!saved) return
    const timer = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(timer)
  }, [saved])

  // Optimistic toggle: flip locally at once, persist, and roll back to
  // the previous value if the write fails.
  const handleToggle = async (): Promise<void> => {
    const next = !value
    const previous = value
    setError(null)
    setValue(next)
    setSaving(true)
    try {
      const savedValue = await setRequire2faApi(next)
      setValue(savedValue)
      setSaved(true)
    } catch (err) {
      setValue(previous)
      setError(err instanceof Error ? err.message : 'Failed to save setting')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard
      icon="shield"
      title="Two-factor authentication"
      sub="When on, every member must set up 2FA to use the app. Members without it are prompted to enrol before they can continue."
    >
      {loading ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading setting…</p>
      ) : (
        <>
          <SettingsToggleRow
            title="Require two-factor authentication for all members"
            desc={value
              ? 'Enforcement is on. Admins can reset a member’s 2FA from their member panel if they lose their device.'
              : 'Enforcement is off — 2FA stays optional per member.'}
            control={
              <SettingsSwitch
                checked={value}
                onChange={() => { void handleToggle() }}
                label="Require two-factor authentication for all members"
                disabled={saving}
              />
            }
          />
          {(saving || saved || error) && (
            <div className="flex flex-wrap items-center gap-[10px]">
              {saving && <span className={S_NOTE}>Saving…</span>}
              {saved && <SettingsSaved />}
              {error && <span className="text-[12.5px] text-casevo-danger" role="alert">{error}</span>}
            </div>
          )}
        </>
      )}
    </SettingsCard>
  )
}

// ── TIMELINE COLOURS PANEL (admin) ───────────────────────────
// Firm-level action-type colours. Admin picks a palette token per
// action type; Save writes the firm scheme (PUT /api/firm/colour-scheme)
// AND updates the cached firmColourScheme so live rows recolour without
// a reload (Stage 2 cascade). Only palette TOKENS are selectable → only
// valid tokens can be saved (backend validates too). Gated by AdminPage's
// page-level admin guard — no per-panel gate needed.
const TimelineColoursPanel: React.FC = () => {
  const setFirmColourScheme = useCasevoStore((s) => s.setFirmColourScheme)
  // Selection per action type (a palette token). Seeded from the shipped
  // defaults so a sensible current state shows before/without a firm
  // scheme; the loaded firm scheme (if any) merges over it below.
  const initialSelections = (): Record<string, string> => {
    const s: Record<string, string> = {}
    for (const t of ACTION_TYPES) s[t.value] = SHIPPED_DEFAULT_SCHEME[t.value]
    return s
  }
  const [selections, setSelections] = useState<Record<string, string>>(initialSelections)
  // The saved scheme — any difference = unsaved (UnsavedGuard below).
  const [savedSelections, setSavedSelections] = useState<Record<string, string>>(initialSelections)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const coloursDirty = ACTION_TYPES.some((t) => selections[t.value] !== savedSelections[t.value])

  // Load the firm scheme on mount; merge each known token over the
  // shipped defaults. Mirrors the other panels' cancelled-flag fetch.
  useEffect(() => {
    let cancelled = false
    getFirmColourSchemeApi()
      .then((firm) => {
        if (cancelled) return
        const next = initialSelections()
        for (const t of ACTION_TYPES) {
          const tok = firm[t.value]
          if (tok && PALETTE[tok]) next[t.value] = tok
        }
        setSelections(next)
        setSavedSelections(next)
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load colours') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // "Saved ✓" auto-fade — same 2s pattern as the other admin panels.
  useEffect(() => {
    if (!saved) return
    const timer = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(timer)
  }, [saved])

  const pick = (actionType: string, token: string): void => {
    setSelections((prev) => ({ ...prev, [actionType]: token }))
    setSaved(false)
  }
  // Reset selections to the shipped defaults — this is the FIRM default
  // reset (the admin can then Save to persist it firm-wide). It does NOT
  // touch any member's per-user override.
  const resetToDefaults = (): void => {
    setSelections(initialSelections())
    setSaved(false)
  }
  const handleSave = async (): Promise<void> => {
    setError(null)
    setSaving(true)
    try {
      const savedScheme = await putFirmColourSchemeApi(selections)
      // Update the cached firm scheme so live timeline rows recolour
      // immediately via the Stage 2 cascade — no reload needed.
      setFirmColourScheme(savedScheme)
      setSavedSelections(selections)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save colours')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard
      icon="palette"
      title="Firm timeline colours"
      sub="Your firm’s colour for each activity type. Applies to every member’s Activity timeline — members can override these for themselves in Settings."
      right={
        <button type="button" onClick={resetToDefaults} disabled={saving || loading} className={S_BTN_GHOST_SM}>
          Reset to defaults
        </button>
      }
    >
      {loading ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading colours…</p>
      ) : (
        <>
          <div className="pt-[8px]">
            <ActionColourAccordion selections={selections} onPick={pick} />
          </div>
          <UnsavedGuard dirty={!saving && coloursDirty} />
          {error && <p className="text-[12.5px] text-casevo-danger mt-[12px]" role="alert">{error}</p>}
          <div className={S_FORMFOOT}>
            {saved && <SettingsSaved />}
            <span className="flex-1" />
            <button type="button" onClick={() => { void handleSave() }} disabled={saving} className={S_BTN_PRIMARY}>
              {saving ? 'Saving…' : 'Save colours'}
            </button>
          </div>
        </>
      )}
    </SettingsCard>
  )
}

// ── PAGE SHELL ───────────────────────────────────────────────
// Admin redesign (mockup casevo-admin-redesign.html): the Settings-sibling
// SECTION-NAV (236px, grouped under 5 headings) + the selected section's
// surface-card panel(s), standard 1180 width + page head. Below lg the nav
// collapses to a scrollable pill bar (same as Settings). The selected
// section is remembered per device; a bell deep-link to a member forces Team.
type AdminSection = 'team' | 'invites' | 'permissions' | 'security' | 'gradeRates' | 'billing' | 'billWording' | 'settlementWording' | 'esign' | 'piSettlements' | 'branding' | 'deadlineTypes' | 'timelineColours' | 'caseHistory' | 'taskNotify' | 'audit'
const ADMIN_SECTION_KEY = 'casevo_admin_section'
const ADMIN_NAV: { heading: string; items: { key: AdminSection; label: string; icon: SettingsIconName }[] }[] = [
  { heading: 'People', items: [
    { key: 'team', label: 'Team', icon: 'users' },
    { key: 'invites', label: 'Invites', icon: 'mail' },
  ] },
  { heading: 'Access & security', items: [
    { key: 'permissions', label: 'Permissions', icon: 'key' },
    { key: 'security', label: 'Security', icon: 'shield' },
  ] },
  { heading: 'Firm setup', items: [
    { key: 'gradeRates', label: 'Grade rates', icon: 'pound' },
    { key: 'billing', label: 'Billing', icon: 'file' },
    // B3 — the statute bill wording + its solicitor approval.
    { key: 'billWording', label: 'Bill wording', icon: 'pen' },
    // B4 — the settlement statement wording + its solicitor approval.
    { key: 'settlementWording', label: 'Settlement wording', icon: 'pen' },
    // B4c — the e-signature pilot (off by default), its consent wording + approval.
    { key: 'esign', label: 'E-signature (pilot)', icon: 'send' },
    { key: 'piSettlements', label: 'PI settlements', icon: 'percent' },
    { key: 'branding', label: 'Branding', icon: 'pen' },
    { key: 'deadlineTypes', label: 'Deadline types', icon: 'flag' },
    { key: 'timelineColours', label: 'Timeline colours', icon: 'palette' },
  ] },
  { heading: 'Policies', items: [
    { key: 'caseHistory', label: 'Case history', icon: 'history' },
    { key: 'taskNotify', label: 'Task notifications', icon: 'bell' },
  ] },
  { heading: 'Oversight', items: [
    { key: 'audit', label: 'Audit log', icon: 'list' },
  ] },
]
const ADMIN_TITLES: Record<AdminSection, [string, string]> = {
  team: ['Team', 'Your firm’s members — roles, billing grades, access and sign-in'],
  invites: ['Invites', 'Invite people to join the firm and manage pending invites'],
  permissions: ['Permissions', 'What members can see and do — firm defaults, with per-person overrides'],
  security: ['Security', 'Firm-wide sign-in requirements'],
  gradeRates: ['Grade rates', 'Hourly billing rates for grades A–D'],
  billing: ['Billing', 'VAT and SRA details, bill numbering and approval'],
  billWording: ['Bill wording', 'The legal wording on bills, and a solicitor’s approval of it'],
  settlementWording: ['Settlement wording', 'The wording on settlement statements, and a solicitor’s approval of it'],
  esign: ['E-signature (pilot)', 'Online signing: the pilot switch, consent wording, links and evidence'],
  piSettlements: ['PI settlements', 'The CFA terms every new case’s CFA starts with'],
  branding: ['Branding', 'Your logo, document colours and fonts'],
  deadlineTypes: ['Deadline types', 'The deadline catalogue used on every case'],
  timelineColours: ['Timeline colours', 'The firm’s colour for each activity type'],
  caseHistory: ['Case history', 'Who can open a case’s History section'],
  taskNotify: ['Task notifications', 'Who is told when someone else completes a task'],
  audit: ['Audit log', 'Every admin-relevant change across the firm'],
}
const isAdminSection = (v: unknown): v is AdminSection =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(ADMIN_TITLES, v)
const initialAdminSection = (): AdminSection => {
  // The place's own section (?view=admin&section=…, a refresh / Back) first.
  const fromPlace = initialPlaceParam('section')
  if (isAdminSection(fromPlace)) return fromPlace
  try {
    const saved = window.localStorage.getItem(ADMIN_SECTION_KEY)
    if (isAdminSection(saved)) return saved
  } catch { /* storage blocked */ }
  return 'team'
}


// B3 — Team member → Job title (printed as "Signed: [name], [job title]…" on
// statute bills they sign). Admin-only (PATCH /api/users/:id/job-title).
const JobTitleRow: React.FC<{ user: FirmUser; fullName: string; disabled: boolean; onSaved: (jobTitle: string | null) => void }> = ({ user, fullName, disabled, onSaved }) => {
  const [value, setValue] = React.useState(user.jobTitle ?? '')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const dirty = value.trim() !== (user.jobTitle ?? '')
  const save = async (): Promise<void> => {
    if (!dirty || busy) return
    setBusy(true); setError(null); setSaved(false)
    try { const r = await updateUserJobTitleApi(user.id, value.trim() || null); onSaved(r.jobTitle); setValue(r.jobTitle ?? ''); setSaved(true) }
    catch (e) { setError(e instanceof Error ? e.message : 'Couldn’t save the job title.') } finally { setBusy(false) }
  }
  return (
    <SettingsRow
      label="Job title"
      action={<button type="button" className={S_BTN_GHOST_SM} disabled={disabled || busy || !dirty} onClick={() => { void save() }}>{busy ? 'Saving…' : 'Save'}</button>}
    >
      <input type="text" value={value} maxLength={80} disabled={disabled || busy} aria-label={`Job title for ${fullName}`} placeholder="e.g. Solicitor, Partner"
        onChange={(e) => { setValue(e.target.value); setSaved(false) }}
        onKeyDown={(e) => { if (e.key === 'Enter') void save() }}
        className={`${sInput(!!error)} max-w-[300px]`} />
      <RowSmall>{error ? <span className="text-casevo-danger">{error}</span> : saved ? 'Saved.' : 'Shown as their position when they sign a bill.'}</RowSmall>
    </SettingsRow>
  )
}

const AdminPage: React.FC = () => {
  const { authUser } = useCasevoStore()
  // Wordmark = "go home" (a navigation like any other).
  const handleBack = (): void => { void navigateTo({ view: 'caseList' }) }
  // The section is part of the place (?view=admin&section=…): choosing one is
  // a history step (Back returns to the section you were on) and asks the
  // unsaved-changes guard first — switching unmounts the panel and its draft.
  const [activeSection, setActiveSection] = useState<AdminSection>(initialAdminSection)
  const showSection = (key: AdminSection): void => {
    setActiveSection(key)
    try { window.localStorage.setItem(ADMIN_SECTION_KEY, key) } catch { /* ignore */ }
  }
  const selectSection = (key: AdminSection): void => {
    if (key === activeSection) return
    void navigateWithin(() => showSection(key))
  }
  usePlaceParam('section', activeSection, (v) => { if (isAdminSection(v)) showSection(v) })
  // Bell deep-link to a member (email-change request) → switch to Team;
  // TeamPanel then opens that member's panel and clears the slot.
  const pendingAdminMemberId = useCasevoStore((s) => s.pendingAdminMemberId)
  useEffect(() => {
    if (pendingAdminMemberId) setActiveSection('team')
  }, [pendingAdminMemberId])
  // Live nav sub-lines (Team: active members).
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  useEffect(() => { void loadFirmUsers() }, [loadFirmUsers])
  const activeCount = firmUsers.filter((u) => u.isActive).length

  // ← Back = the place you were on; hidden with no in-app history.
  const backButton = <BackButton />

  // Defence-in-depth — the sidebar nav link is already admin-gated.
  if (!authUser || authUser.role !== 'admin') {
    return (
      <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
        <AppHeader onWordmarkClick={handleBack} leftHamburger={<SidebarHamburger />} leftSlot={backButton} />
        <div className="max-w-[1180px] mx-auto px-4 sm:px-[36px] pt-6 sm:pt-[30px] pb-[60px]">
          <p className={S_NOTE}>Admins only. Please contact your firm admin if you need access.</p>
        </div>
      </div>
    )
  }

  const navSub = (key: AdminSection): string | null => {
    if (key === 'team') return firmUsers.length ? `${activeCount} active` : null
    // The permissions actually listed (every key, grouped).
    if (key === 'permissions') return String(PERMISSION_GROUPS.reduce((n, g) => n + g.perms.length, 0))
    return null
  }
  const [title, sub] = ADMIN_TITLES[activeSection]

  return (
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      <AppHeader onWordmarkClick={handleBack} leftHamburger={<SidebarHamburger />} leftSlot={backButton} />
      <div className="max-w-[1180px] mx-auto px-4 sm:px-[36px] pt-6 sm:pt-[30px] pb-[60px]">
        {/* ── Page head (.phead) ── */}
        <div className="mb-[18px] lg:mb-[22px]">
          <p className="text-[12px] tracking-[0.18em] uppercase text-casevo-brand mb-[6px]">Admin</p>
          <h1 className="text-[22px] sm:text-[26px] font-medium tracking-[-0.01em] text-casevo-forest leading-[1.15]">{title}</h1>
          <p className="text-[13.5px] text-casevo-text-soft mt-[5px]">{sub}</p>
        </div>

        {/* ── Mobile / tablet: nav collapses to a scrollable pill bar ── */}
        <nav aria-label="Admin sections" className="lg:hidden -mx-4 px-4 mb-[16px] overflow-x-auto">
          <div className="flex gap-[6px] w-max pb-[2px]">
            {ADMIN_NAV.flatMap((g) => g.items).map((s) => (
              <SectionNavPill key={s.key} icon={s.icon} label={s.label} active={activeSection === s.key} onClick={() => selectSection(s.key)} />
            ))}
          </div>
        </nav>

        {/* ── Split (.split): 236px nav | panel ── */}
        <div className="lg:grid lg:grid-cols-[236px_minmax(0,1fr)] lg:gap-[26px] lg:items-start">
          <nav aria-label="Admin sections" className="hidden lg:block sticky top-[20px]">
            <div className="flex gap-[10px] items-center px-[6px] pt-[4px] pb-[14px]">
              <UserAvatar userId={authUser.id} fallbackName={`${authUser.firstName} ${authUser.lastName}`.trim() || authUser.email} size="nav" title={false} />
              <div className="min-w-0">
                <b className="block text-[14px] font-semibold text-casevo-forest truncate">{authUser.firmName || 'Your firm'}</b>
                <small className="block text-[12px] text-casevo-text-soft truncate">
                  Admin{firmUsers.length ? ` · ${activeCount} active member${activeCount === 1 ? '' : 's'}` : ''}
                </small>
              </div>
            </div>
            {ADMIN_NAV.map((group) => (
              <div key={group.heading}>
                <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft px-[12px] pt-[12px] pb-[6px]">
                  {group.heading}
                </div>
                {group.items.map((s) => (
                  <SectionNavItem key={s.key} dense icon={s.icon} label={s.label} active={activeSection === s.key} sub={navSub(s.key)} onClick={() => selectSection(s.key)} />
                ))}
              </div>
            ))}
          </nav>

          {/* ── Panel (.stack) — switching sections unmounts the previous
              panel, so each re-fetches fresh state when reopened. ── */}
          <div className="flex flex-col gap-[18px] min-w-0">
            {activeSection === 'team' && <TeamPanel authUserId={authUser.id} onInvite={() => selectSection('invites')} />}
            {activeSection === 'invites' && <InvitesPanel />}
            {activeSection === 'permissions' && <PermissionsPanel />}
            {activeSection === 'security' && <Require2faPanel />}
            {activeSection === 'gradeRates' && <GradeRatesPanel />}
            {activeSection === 'billWording' && <BillWordingPanel onOpenBilling={() => selectSection('billing')} />}
            {activeSection === 'settlementWording' && <SettlementWordingPanel onOpenBillWording={() => selectSection('billWording')} />}
            {activeSection === 'esign' && <EsignSettingsPanel />}
            {activeSection === 'billing' && <BillingSettingsPanel onOpenBranding={() => selectSection('branding')} onOpenPiSettlements={() => selectSection('piSettlements')} onOpenTeam={() => selectSection('team')} />}
            {activeSection === 'piSettlements' && <PiSettlementDefaultsPanel />}
            {activeSection === 'branding' && <BrandingSettingsPanel />}
            {activeSection === 'deadlineTypes' && <DeadlineTypesPanel />}
            {activeSection === 'timelineColours' && <TimelineColoursPanel />}
            {activeSection === 'caseHistory' && <CaseHistoryVisibilityPanel />}
            {activeSection === 'taskNotify' && <TaskCompleteNotifyPanel />}
            {activeSection === 'audit' && <AuditLogPanel authUserId={authUser.id} />}
          </div>
        </div>
      </div>
    </div>
  )
}

// The firm-defaults editor leaves out per-person-only grants (e.g. "View all
// billing"), which have no firm-wide default.
const FIRM_DEFAULT_GROUPS = PERMISSION_GROUPS
  .map((g) => ({ ...g, perms: g.perms.filter((p) => !PER_USER_ONLY_PERMISSIONS.has(p.key)) }))
  .filter((g) => g.perms.length > 0)

// ── PERMISSIONS PANEL (Option A) ─────────────────────────────
// ONE card with a "Firm defaults | By member" switch. Permissions are
// GROUPED by area (Tasks / Cases / Appointments / Client notes — PERMISSION_GROUPS).
//  • Firm defaults: each permission's firm-wide on/off switch → PATCH
//    /api/admin/firm/permission-defaults (optimistic + rollback). This is
//    what a member inherits without an override.
//  • By member: pick a non-admin member → their tristate overrides
//    (UserPermissionsEditor, the same editor the member panel uses).
// Admins always have every permission, so they aren't listed.
const PermissionsPanel: React.FC = () => {
  const [view, setView] = useState<'defaults' | 'member'>('defaults')
  const [defaults, setDefaults] = useState<EffectivePermissions | null>(null)
  const [savingKey, setSavingKey] = useState<PermissionKey | null>(null)
  const [error, setError] = useState<string | null>(null)
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  const [memberId, setMemberId] = useState<string | null>(null)
  // Per-member override counts for the picker pills + the switch badge.
  const [ovCounts, setOvCounts] = useState<Record<string, number>>({})

  useEffect(() => { void loadFirmUsers() }, [loadFirmUsers])
  useEffect(() => {
    let cancelled = false
    getFirmPermissionDefaultsApi()
      .then((d) => { if (!cancelled) setDefaults(d) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load defaults') })
    return () => { cancelled = true }
  }, [])

  // Non-admin members (admins have everything) — active first, then A–Z.
  const members = firmUsers
    .filter((u) => u.role !== 'admin')
    .sort((a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1)
      || (`${a.firstName} ${a.lastName}`).localeCompare(`${b.firstName} ${b.lastName}`))
  const memberIdsKey = members.map((m) => m.id).join(',')
  const selectedId = memberId && members.some((m) => m.id === memberId) ? memberId : (members[0]?.id ?? null)
  const selected = members.find((m) => m.id === selectedId) ?? null

  // Load every member's override count once the member list is known.
  useEffect(() => {
    if (!memberIdsKey) return
    let cancelled = false
    const ids = memberIdsKey.split(',')
    Promise.all(ids.map((id) => getUserPermissionsApi(id)
      .then((o) => [id, Object.keys(o).length] as const)
      .catch(() => [id, 0] as const)))
      .then((rows) => { if (!cancelled) setOvCounts(Object.fromEntries(rows)) })
    return () => { cancelled = true }
  }, [memberIdsKey])
  const totalOverrides = Object.values(ovCounts).reduce((n, c) => n + c, 0)

  const toggle = async (key: PermissionKey, next: boolean): Promise<void> => {
    if (savingKey) return
    setSavingKey(key)
    setError(null)
    const prev = defaults
    setDefaults((d) => (d ? { ...d, [key]: next } : d)) // optimistic
    try {
      const saved = await setFirmPermissionDefaultsApi({ [key]: next })
      setDefaults(saved)
    } catch (err) {
      setDefaults(prev) // rollback
      setError(err instanceof Error ? err.message : 'Failed to save default')
    } finally {
      setSavingKey(null)
    }
  }
  const jumpTo = (key: string): void => {
    document.getElementById(`perm-group-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const viewSwitch = (
    <div role="tablist" aria-label="Permissions view" className="flex flex-wrap gap-[6px]">
      <button type="button" role="tab" aria-selected={view === 'defaults'} onClick={() => setView('defaults')} className={`${A_CHIP_BASE} ${view === 'defaults' ? A_CHIP_ON : A_CHIP_OFF}`}>
        Firm defaults
      </button>
      <button type="button" role="tab" aria-selected={view === 'member'} onClick={() => setView('member')} className={`${A_CHIP_BASE} ${view === 'member' ? A_CHIP_ON : A_CHIP_OFF}`}>
        By member
        {totalOverrides > 0 && <span className={A_CHIP_COUNT}>{totalOverrides} override{totalOverrides === 1 ? '' : 's'}</span>}
      </button>
    </div>
  )

  if (view === 'defaults') {
    return (
      <SettingsCard
        icon="key"
        title="Permission defaults"
        sub="Every member inherits these unless you set a per-person override. Admins always have every permission."
        right={viewSwitch}
        flushBody
      >
        {/* Jump chips — scroll to an area. */}
        <div className="flex flex-wrap gap-[6px] pt-[12px]">
          {FIRM_DEFAULT_GROUPS.map((g) => (
            <button key={g.key} type="button" onClick={() => jumpTo(g.key)} className={`${A_CHIP_BASE} ${A_CHIP_OFF}`}>
              <SIcon name={g.icon} size={13} />
              {g.label}
              <span className={A_CHIP_COUNT}>{g.perms.length}</span>
            </button>
          ))}
        </div>
        {defaults === null ? (
          <p className={`${S_NOTE} py-[13px]`}>{error ? '' : 'Loading…'}</p>
        ) : (
          FIRM_DEFAULT_GROUPS.map((g) => {
            const onCount = g.perms.filter((p) => defaults[p.key]).length
            return (
              <section key={g.key} id={`perm-group-${g.key}`} aria-label={`${g.label} permissions`} className="scroll-mt-[20px]">
                <PermGroupHeading icon={g.icon} label={g.label} count={`${onCount} of ${g.perms.length} on`} />
                {g.perms.map((p, i) => {
                  const on = defaults[p.key]
                  const { label, help } = PERMISSION_LABELS[p.key]
                  return (
                    <div key={p.key} className={i === 0 ? PERM_ROW_FIRST : PERM_ROW}>
                      <div className="flex gap-[12px] items-start min-w-0">
                        <PermIcon icon={p.icon} />
                        <div className="min-w-0">
                          <div className="text-[14px] font-medium text-casevo-forest">{label}</div>
                          <div className="text-[12.5px] text-casevo-text-soft mt-[3px] leading-[1.45] max-w-[560px]">{help}</div>
                        </div>
                      </div>
                      <SettingsSwitch
                        checked={on}
                        onChange={() => { void toggle(p.key, !on) }}
                        label={`${label} — firm default: ${on ? 'on' : 'off'}`}
                        disabled={savingKey === p.key}
                      />
                    </div>
                  )
                })}
              </section>
            )
          })
        )}
        {error && <p className="text-[12.5px] text-casevo-danger mt-[12px]" role="alert">{error}</p>}
      </SettingsCard>
    )
  }

  return (
    <SettingsCard
      icon="key"
      title="Permissions by member"
      sub="Pick a person, then override any default. “Inherit” follows the firm default shown in brackets."
      right={viewSwitch}
      flushBody
    >
      {members.length === 0 ? (
        <p className={`${S_NOTE} pt-[12px]`}>Everyone in the firm is an admin — admins always have every permission.</p>
      ) : (
        <>
          {/* .mp — member picker pills (with override counts). */}
          <div className="flex gap-[8px] overflow-x-auto pt-[12px] pb-[2px]" role="listbox" aria-label="Choose a member">
            {members.map((m) => {
              const on = m.id === selectedId
              const n = ovCounts[m.id] ?? 0
              return (
                <button
                  key={m.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => setMemberId(m.id)}
                  className={`flex items-center gap-[8px] pl-[6px] pr-[12px] py-[6px] rounded-full border text-[13px] whitespace-nowrap transition-colors focus:outline-none casevo-focus ${
                    on
                      ? 'border-casevo-brand bg-casevo-mint text-casevo-forest dark:text-[#cfeedd] font-semibold'
                      : 'border-casevo-border bg-casevo-surface text-casevo-brand dark:text-casevo-mid hover:border-casevo-mid'
                  } ${m.isActive ? '' : 'opacity-[0.62]'}`}
                >
                  <UserAvatar userId={m.id} fallbackName={`${m.firstName} ${m.lastName}`.trim() || m.email} fallbackLook={{ colour: m.avatarColour, style: m.avatarStyle, photoUrl: m.avatarPhotoUrl }} size="pill" title={false} />
                  {`${m.firstName} ${m.lastName}`.trim() || m.email}
                  {n > 0 && <OverrideTag>{n}</OverrideTag>}
                </button>
              )
            })}
          </div>
          {selected && (
            <UserPermissionsEditor
              key={selected.id}
              userId={selected.id}
              onCountChange={(n) => setOvCounts((c) => (c[selected.id] === n ? c : { ...c, [selected.id]: n }))}
              header={
                <div className="flex items-center gap-[10px] min-w-0">
                  <UserAvatar userId={selected.id} fallbackName={`${selected.firstName} ${selected.lastName}`.trim() || selected.email} fallbackLook={{ colour: selected.avatarColour, style: selected.avatarStyle, photoUrl: selected.avatarPhotoUrl }} size="sm" title={false} />
                  <div className="min-w-0">
                    <div className="text-[14px] font-medium text-casevo-forest truncate">{`${selected.firstName} ${selected.lastName}`.trim() || selected.email}</div>
                    <div className="text-[12.5px] text-casevo-text-soft">
                      User{selected.billingGrade ? ` · Grade ${selected.billingGrade}` : ''}{selected.isActive ? '' : ' · Deactivated'}
                      {' · '}{ovCounts[selected.id] ?? 0} override{(ovCounts[selected.id] ?? 0) === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              }
            />
          )}
        </>
      )}
    </SettingsCard>
  )
}

// ── PER-USER PERMISSIONS EDITOR ──────────────────────────────
// Used by Permissions → By member AND the Team member panel. Loads the
// target's raw overrides + the firm defaults; permissions are GROUPED by
// area, each with the TRISTATE control Inherit (firm On/Off) / Granted /
// Denied. A change PATCHes { key: true|false|null } (null = inherit),
// optimistic + rollback. "Reset all to inherit" clears every override.
const UserPermissionsEditor: React.FC<{
  userId: string
  // compact = no help lines (member panel, where space is tighter).
  compact?: boolean
  header?: React.ReactNode
  onCountChange?: (n: number) => void
}> = ({ userId, compact = false, header, onCountChange }) => {
  const [overrides, setOverrides] = useState<UserPermissionOverrides | null>(null)
  const [defaults, setDefaults] = useState<EffectivePermissions | null>(null)
  const [savingKey, setSavingKey] = useState<PermissionKey | 'ALL' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const countCb = useRef(onCountChange)
  countCb.current = onCountChange

  useEffect(() => {
    let cancelled = false
    setOverrides(null)
    setError(null)
    Promise.all([getUserPermissionsApi(userId), getFirmPermissionDefaultsApi()])
      .then(([ov, df]) => { if (!cancelled) { setOverrides(ov); setDefaults(df) } })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load permissions') })
    return () => { cancelled = true }
  }, [userId])

  // Report the live override count (picker pill badges).
  useEffect(() => {
    if (overrides) countCb.current?.(Object.keys(overrides).length)
  }, [overrides])

  const setOne = async (key: PermissionKey, value: boolean | null): Promise<void> => {
    if (savingKey) return
    setSavingKey(key)
    setError(null)
    const prev = overrides
    setOverrides((o) => {
      const next = { ...(o ?? {}) }
      if (value === null) delete next[key]
      else next[key] = value
      return next
    })
    try {
      const saved = await setUserPermissionsApi(userId, { [key]: value })
      setOverrides(saved)
    } catch (err) {
      setOverrides(prev)
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSavingKey(null)
    }
  }

  const resetAll = async (): Promise<void> => {
    if (savingKey || !overrides || Object.keys(overrides).length === 0) return
    setSavingKey('ALL')
    setError(null)
    const prev = overrides
    setOverrides({})
    try {
      const clear = Object.fromEntries(Object.keys(prev).map((k) => [k, null])) as Partial<Record<PermissionKey, null>>
      const saved = await setUserPermissionsApi(userId, clear)
      setOverrides(saved)
    } catch (err) {
      setOverrides(prev)
      setError(err instanceof Error ? err.message : 'Failed to reset')
    } finally {
      setSavingKey(null)
    }
  }

  const hasOverrides = overrides ? Object.keys(overrides).length > 0 : false
  const stateOf = (key: PermissionKey): TriState => {
    const v = overrides?.[key]
    if (v === true) return 'granted'
    if (v === false) return 'denied'
    return 'inherit'
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-[10px] pt-[14px]">
        {header ?? <span className="text-[11px] font-semibold tracking-[0.1em] uppercase text-casevo-brand dark:text-casevo-mid">Permissions</span>}
        <span className="flex-1" />
        <button type="button" onClick={() => { void resetAll() }} disabled={!hasOverrides || savingKey !== null} className={S_BTN_GHOST_SM}>
          <SIcon name="refresh" size={13} />
          Reset all to inherit
        </button>
      </div>
      <p className={`${S_NOTE} mt-[8px]`}>
        <strong className="font-semibold text-casevo-forest">Inherit</strong> follows the firm default (in brackets) ·{' '}
        <strong className="font-semibold text-casevo-forest">Granted</strong> turns it on for this person ·{' '}
        <strong className="font-semibold text-casevo-forest">Denied</strong> turns it off.
      </p>
      {overrides === null || defaults === null ? (
        <p className={`${S_NOTE} py-[13px]`}>{error ? '' : 'Loading…'}</p>
      ) : (
        PERMISSION_GROUPS.map((g) => (
          <section key={g.key} aria-label={`${g.label} permissions`}>
            <PermGroupHeading icon={g.icon} label={g.label} />
            {g.perms.map((p, i) => {
              const st = stateOf(p.key)
              const { label, help } = PERMISSION_LABELS[p.key]
              return (
                <div key={p.key} className={`${i === 0 ? PERM_ROW_FIRST : PERM_ROW} max-sm:grid-cols-1`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-[8px] text-[14px] font-medium text-casevo-forest">
                      {label}
                      {st !== 'inherit' && <OverrideTag>Overridden</OverrideTag>}
                    </div>
                    {!compact && <div className="text-[12.5px] text-casevo-text-soft mt-[3px] leading-[1.45] max-w-[560px]">{help}</div>}
                  </div>
                  <TristateControl
                    value={st}
                    firmDefault={defaults[p.key]}
                    label={label}
                    disabled={savingKey !== null}
                    onChange={(next) => { void setOne(p.key, next === 'inherit' ? null : next === 'granted') }}
                  />
                </div>
              )
            })}
          </section>
        ))
      )}
      {error && <p className="text-[12.5px] text-casevo-danger mt-[12px]" role="alert">{error}</p>}
    </div>
  )
}

export default AdminPage
